import { Injectable } from 'framework/di';

import { CartAPI } from '../../api/cart';
import { getCartSelectorAndUpdateAction } from '../../cart/selectors';
import {
  handleClickEmbeddedBundle,
  initDynamicBundle,
  renderRecommendationForSearchPage,
} from '../../dynamicBundle';
import { DomHelper } from '../../helpers/dom.helper';
import { appIntegration } from '../../integration';
import { quickAddToCart } from '../../product-item';
import { quickView } from '../../product-item/quick-view';
import { handleSearchResultTabs } from '../../search';
import { AppService } from '../app/app.service';
import { CartController } from '../cart/cart.controller';
import {
  COLLAPSE_STATE,
  COLLECTION_SELECTED,
  FILTER_MOBILE_BUTTON_BACK,
  FILTER_MOBILE_BUTTON_CLOSE,
  FILTER_MOBILE_BUTTON_CLOSE_CLASS,
  FILTER_MOBILE_BUTTON_HIDDEN,
  FILTER_MOBILE_FULL_HEIGHT_CLASS,
  FILTER_MOBILE_FULL_HEIGHT_SELECTOR,
  FILTER_MOBILE_WRAPPER_SELECTOR,
  FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS,
  FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS,
  FILTER_OPTION_SEARCH_OPTION_SELECTOR,
  FILTER_TREE_MOBILE_BUTTON_CLEAR,
  FILTER_TREE_MOBILE_TOOL_BAR,
  FILTER_TREE_RESET_DELAY,
  FILTER_TREE_STATE_KEY,
  KEYS,
  LIMIT,
  LIMIT_SETTING,
  SELECTED_PRODUCT_ID,
  SELECTED_PRODUCT_PAGE,
  SWATCH_OPTION_TYPE,
  SWATCH_PREFIX,
} from './filter.constants';
import { FilterHandler } from './filter.handler';
import { FilterHelper } from './filter.helper';
import {
  EVENT_NAMES,
  EventHandler,
  EventPayload,
  FilterAPIAdditionParams,
  FilterAPIResponse,
} from './filter.model';
import { FilterRender } from './filter.render';
import { FilterService } from './filter.service';
import { FilterStickyController } from './filter.sticky.controller';
import { FilterStore } from './filter.store';
import { FilterTranslationService } from './filter.translation.service';

/**
 * Main controller for managing filter functionality in collection and search pages.
 *
 * This controller orchestrates all filter-related operations including:
 * - Filter tree rendering and state management
 * - Product list updates and pagination
 * - Event handling for user interactions
 * - Price transformation and currency formatting
 * - Mobile and desktop responsive behaviors
 * - Integration with cart, bundles, and recommendations
 *
 * The class is designed to be extended by Technical Support teams for shop-specific
 * customizations. Protected methods provide clear extension points for modifying
 * filter behavior, product rendering, and UI interactions.
 *
 * @example
 * Extend to customize price display for specific shops:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
 *   return class CustomFilterController extends FilterController {
 *     protected async buildPriceHTML(priceMin, compareAtPriceMin, priceMax, options) {
 *       // Add custom price formatting for VIP customers
 *       const html = await super.buildPriceHTML(priceMin, compareAtPriceMin, priceMax, options);
 *       if (this.isVipCustomer()) {
 *         return html + '<span class="vip-badge">VIP Price</span>';
 *       }
 *       return html;
 *     }
 *   };
 * });
 * ```
 *
 * @example
 * Override product list rendering behavior:
 * ```javascript
 * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
 *   return class CustomFilterController extends FilterController {
 *     protected async handleFullPageReplace(domId, html) {
 *       // Add custom analytics before replacing DOM
 *       this.trackFilterChange();
 *       await super.handleFullPageReplace(domId, html);
 *     }
 *   };
 * });
 * ```
 */
@Injectable
export class FilterController {
  private volumeBundleModule: any = null;
  constructor(
    private appService: AppService,
    private filterHelper: FilterHelper,
    private filterStore: FilterStore,
    private filterService: FilterService,
    private filterTranslationService: FilterTranslationService,
    private domHelper: DomHelper,
    private filterRender: FilterRender,
    private filterStickyController: FilterStickyController,
    private filterHandler: FilterHandler,
    private cartController: CartController
  ) {}

  /**
   * The active DOM context for filter operations.
   *
   * This document reference is set during filter initialization and can be
   * different from the global document when working with specific DOM contexts,
   * multiple filter instances, or when the filter is rendered in isolated containers.
   *
   * @returns The HTMLElement serving as the root document context for this filter instance
   *
   * @remarks
   * All DOM queries within the filter should use this context instead of the global
   * `document` to ensure operations are scoped correctly. This is particularly
   * important for shops with multiple collection pages or dynamic filter instances.
   */
  protected get document(): HTMLElement {
    return this.filterStore.getState('document') as HTMLElement;
  }

  /**
   * Initializes all event listeners for the filter system.
   * Sets up handlers for user interactions, filter changes, and UI updates.
   *
   * Registers handlers for:
   * - Filter tree interactions (collapse, apply, reset, clear)
   * - Product interactions (add to cart, quick view, quick add)
   * - Toolbar actions (sort, pagination, view mode, product limit)
   * - Mobile filter UI (show/hide, back navigation)
   * - Desktop filter UI (toggle, sticky behavior)
   * - Bundle interactions (embedded, volume)
   * - Search and state management
   *
   * @remarks
   * Event handlers are registered through the filterHelper.on() method and use
   * the EVENT_NAMES enumeration for type-safe event registration. Many handlers
   * are wrapped in createHandler() for error handling and debugging.
   *
   * @example
   * Extend to add custom event handlers:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async initEventLineners() {
   *       await super.initEventLineners();
   *
   *       // Add custom event listener
   *       this.filterHelper.on('CUSTOM_EVENT', (payload) => {
   *         this.handleCustomEvent(payload);
   *       });
   *     }
   *   };
   * });
   * ```
   *
   * @example
   * Override specific event handler:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async initEventLineners() {
   *       // Call parent to register all handlers
   *       await super.initEventLineners();
   *
   *       // Override specific handler
   *       this.filterHelper.on('FILTER_CHANGED', async (payload) => {
   *         this.trackFilterChange(payload);
   *         // Custom handling
   *       });
   *     }
   *   };
   * });
   * ```
   */
  async initEventLineners() {
    const handlers: Record<string, EventHandler> = {
      [EVENT_NAMES.COLLAPSE_CHANGED]: () => {
        this.filterRender.handleCollapsedChange();
      },
      [EVENT_NAMES.RENDER_REFINE_BY]: () => {
        this.filterHelper.createHandler(
          () => this.filterRender.renderRefineBy(),
          'handleRenderRefineBy'
        )();
      },
      [EVENT_NAMES.FILTER_CHANGED]: async (payload) => {
        await this.handleFilterTree();
        await this.setupCoreUI();
        this.filterHelper.emit(EVENT_NAMES.RENDER_REFINE_BY);
      },
      [EVENT_NAMES.FILTER_RESET]: async (payload) => {
        await this.handleResetFilter();
      },
      [EVENT_NAMES.FORCE_RENDER_CHANGED]: async (payload) => {
        await this.handleForceRenderChange();
      },
      [EVENT_NAMES.FILTER_OPTION_RENDERED]: () => {
        // Custom event after filter option is rendered
        const renderFiltered = new CustomEvent('afterRenderHtmlForFilter');
        window.dispatchEvent(renderFiltered);
      },
      [EVENT_NAMES.VIEWMORE_TRIGGERED]: async (option) => {
        // Custom event after filter option is rendered
        const viewMoreTrigger = this.filterStore.getState('viewMoreTrigger');
        if (viewMoreTrigger) {
          await this.filterRender.renderViewMore(
            viewMoreTrigger.filterOptionId,
            viewMoreTrigger.displayType
          );
        }
      },
      [EVENT_NAMES.AFTER_DOCUMENT_CHANGED]: () => {
        if (this.filterHelper.isSearchPage()) {
          this.renderRecommendationForSearchPage();
          this.renderVolumeBundleForSearchPage();
        }
        this.appIntegration();
        this.handleTooltip();
        const blockType = this.filterStore.getState('blockType') as string;
        const afterRenderFn =
          this.appService.TAEAppConfig.customization?.[blockType]?.afterRender ??
          (this.appService.TAEAppConfig.customization as any)?.afterRender;
        afterRenderFn?.();
        const block: any = this.appService.blockByType(blockType);
        block?.onFirstRender?.(); //backward compatibility
        this.filterTranslationService.applyTranslationsToFilterOptions();
      },
      [EVENT_NAMES.NEW_DOCUMENT_CHANGED]: async () => {
        const { filterLayout } = this.appService.filterSettings;
        const blockType = this.filterStore.getState('blockType') as string;

        const block: any = this.appService.blockByType(blockType);
        block?.onFirstRender?.(); //backward compatibility
        this.filterTranslationService.applyTranslationsToFilterOptions();

        this.filterStickyController.blockFilterOpening();
        const stickyStatus = this.filterStickyController.getStickyStatus();
        if (stickyStatus.stickyEnabled) {
          if (stickyStatus.stickyFilterOnMobile && this.filterHelper.isMobile()) {
            await this.filterStickyController.initializeMobile();
          }
          if (stickyStatus.stickyFilterOnDesktop && !this.filterHelper.isMobile()) {
            if (filterLayout === 'vertical') {
              await this.filterStickyController.initializeVertical();
            } else if (filterLayout === 'horizontal') {
              await this.filterStickyController.initializeHorizontal();
            }
          }
        }
      },
      // Filter tree handlers
      [EVENT_NAMES.OPTION_LIST]: (payload) => {
        const { action } = (payload as any) || {};
        if (action) {
          this.filterHelper.createHandler(
            () => this.filterRender.optionList(action),
            'handleOptionList'
          )();
        }
      },
      [EVENT_NAMES.CLEAR_ALL_FILTER]: async (payload) => {
        this.filterHelper.createHandler(
          () => this.filterRender.clearAllFilters(),
          'handleClearAllFilters'
        )();
      },
      [EVENT_NAMES.CLEAR_FILTER]: async (payload) => {
        const { action } = payload as any;
        this.filterHelper.createHandler(
          () => this.filterRender.clearFilter(action),
          'handleClearFilter'
        )();
      },
      [EVENT_NAMES.REMOVE_FILTER_FROM_REFINE_BY]: async (payload) => {
        const { action } = payload as any;
        this.filterHelper.createHandler(
          () => this.filterRender.clearFilter(action),
          'handleRemoveFilterFromRefineBy'
        )();
      },
      [EVENT_NAMES.OPTION_COLLECTION]: (payload) => {
        const { action } = payload as any;
        this.filterHelper.createHandler(
          async () => await this.filterRender.handleCollectionOption(action),
          'handleOptionCollection'
        )();
      },
      [EVENT_NAMES.FILTER_OPTION_COLLAPSE]: (payload) => {
        const { action } = payload as any;
        this.filterHelper.createHandler(
          () => this.filterOptionCollapse(action),
          'handleFilterOptionCollapse'
        )();
      },
      [EVENT_NAMES.APPLY_FILTER_OPTION]: async (payload) => {
        const { action } = payload as any;
        this.filterHelper.createHandler(
          () => this.filterRender.applyFilterOption(action),
          'handleApplyFilterOption'
        )();
      },
      [EVENT_NAMES.RESET_FILTER_OPTION]: async (payload) => {
        const { action } = payload as any;
        this.filterHelper.createHandler(
          () => this.filterRender.resetFilterOption(action),
          'handleResetFilterOption'
        )();
      },
      [EVENT_NAMES.EXPAND_COLLAPSE_MULTI_LEVEL]: (payload) => {
        const { action } = (payload as any) || {};
        if (action) {
          this.filterHelper.createHandler(
            () => this.filterRender.expandCollapseMultiLevel(action),
            'handleExpandCollapseMultiLevel'
          )();
        }
      },
      [EVENT_NAMES.ADD_TO_CART]: async (payload) => {
        const { action, target } = (payload as any) || {};
        if (action) {
          this.filterHelper.createHandler(
            async () => await this.cartController.addToCart({ ...action }, target),
            'handleAddToCartClick'
          )();
        }
      },
      [EVENT_NAMES.QUICK_VIEW]: (payload) => {
        const block = this.appService.blockByType('filter');

        const { action, target } = (payload as any) || {};
        if (action) {
          this.filterHelper.createHandler(
            () => quickView(block as any, action, target),
            'handleQuickViewClick'
          )();
        }
      },
      [EVENT_NAMES.QUICK_ADD_TO_CART]: (payload) => {
        const block = this.appService.blockByType('filter');

        const { action, target } = (payload as any) || {};
        if (action) {
          this.filterHelper.createHandler(
            () => quickAddToCart(block as any, action, target),
            'handleQuickAddToCartClick'
          )();
        }
      },
      [EVENT_NAMES.CLOSE_QUICK_ADD_TO_CART]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterHandler.handleCloseQuickAddToCart(target),
            'handleCloseQuickAddToCart'
          )();
        }
      },
      [EVENT_NAMES.GO_TO_PRODUCT_DETAIL]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterHandler.handleClickProductItem(target),
            'handleClickProductItem'
          )();
        }
      },
      // Toolbar handlers
      [EVENT_NAMES.HANDLE_SORT]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(() => this.filterHandler.handleSort(target), 'sort')();
        }
      },
      [EVENT_NAMES.SORT_CHANGED]: () => {
        // Update sort dropdown UI when sort state changes
        this.filterHandler.updateSortUI();
      },
      [EVENT_NAMES.PAGINATION]: async (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(async () => {
            const { newPage, behavior } = this.filterHandler.handlePagination(target);
            await this.processPaginatingAction(newPage, behavior);
          }, 'pagination')();
        }
      },
      [EVENT_NAMES.HANDLE_TOGGLE_SHOW_LIMIT_LIST]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterHandler.handleToggleShowLimitList(target),
            'handleToggleShowLimitList'
          )();
        }
      },
      [EVENT_NAMES.LIMIT_LIST_CHANGED]: () => {
        // Update limit list dropdown UI when limit list state changes
        this.filterHandler.updateLimitListUI();
      },
      [EVENT_NAMES.HANDLE_LIMIT_LIST_OPTION_CLICK]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterHandler.handleLimitListOptionClick(target),
            'handleLimitListOptionClick'
          )();
        }
      },
      [EVENT_NAMES.HANDLE_VIEW_AS_CLICK]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterHandler.handleViewAsClick(target),
            'handleViewAsClick'
          )();
        }
      },
      // Mobile filter handlers
      [EVENT_NAMES.SHOW_MOBILE_FILTER_TREE]: (payload) => {
        this.filterHelper.createHandler(
          () => this.filterRender.showMobileFilterTree(),
          'handleShowMobileFilterTreeClick'
        )();
      },
      [EVENT_NAMES.CLOSE_MOBILE_FILTER_TREE]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterRender.closeMobileFilterTree(),
            'handleCloseMobileFilterTreeClick'
          )();
        }
      },
      [EVENT_NAMES.BACK_FILTER_TREE_MOBILE]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterRender.backFilterTreeMobile(),
            'handleBackFilterTreeMobileClick'
          )();
        }
      },
      // Desktop filter handlers
      [EVENT_NAMES.TOGGLE_FILTER_TREE_BUTTON_DESKTOP]: (payload) => {
        this.filterHelper.createHandler(
          () => this.filterRender.toggleFilterTreeButtonDesktop(),
          'handleToggleFilterTreeButtonDesktop'
        )();
      },
      // UI utility handlers
      [EVENT_NAMES.SCROLL_TO_TOP]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.domHelper.scrollToTop({ behavior: 'auto' }),
            'handleScrollToTop'
          )();
        }
      },
      // Bundle handlers
      [EVENT_NAMES.HANDLE_CLICK_EMBEDDED_BUNDLE]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          const block = this.appService.blockByType('filter');
          this.filterHelper.createHandler(async () => {
            handleClickEmbeddedBundle(block as any, target);
          }, 'handleEmbeddedBundleClick')();
        }
      },
      [EVENT_NAMES.HANDLE_CLICK_VOLUME_BUNDLE]: async (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          const block = this.appService.blockByType('filter');
          this.filterHelper.createHandler(async () => {
            import(
              /* webpackChunkName: 'volume-bundle' */
              '../../volume-bundle'
            ).then((module) => {
              const { handleClickVolumeBundle } = module;
              handleClickVolumeBundle(block as any, target);
            });
          }, 'handleVolumeBundleClick')();
        }
      },
      [EVENT_NAMES.LOAD_STICKY_HORIZONTAL_DESKTOP]: (payload) => {
        this.filterStickyController.loadStickyFilterTreeHorizontalExpandContent();
      },
      [EVENT_NAMES.LOAD_STICKY_VERTICAL_DESKTOP]: (payload) => {
        this.filterStickyController.loadStickyFilterTreeExpandContent();
      },
      [EVENT_NAMES.LOAD_STICKY_MOBILE]: (payload) => {
        this.filterStickyController.loadStickyFilterTreeMobileExpandContent();
      },
      [EVENT_NAMES.HANDLE_OUTSIDE_SORT_CLICK]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(async () => {
            this.filterHandler.handleClickOutsideSort(target);
          }, 'handleOutsideSortClick')();
        }
      },
      [EVENT_NAMES.HANDLE_OUTSIDE_LIMIT_LIST_CLICK]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(async () => {
            this.filterHandler.handleClickOutsideLimitList(target);
          }, 'handleOutsideLimitListClick')();
        }
      },
      [EVENT_NAMES.HANDLE_OUTSIDE_MINI_POPUP_CLICK]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(async () => {
            this.filterHandler.handleClickOutsideMiniPopup(target);
          }, 'handleOutsideLMiniPopupClick')();
        }
      },
      [EVENT_NAMES.DEBOUNCE_INCOLLECTION_SEARCH]: (payload) => {
        const { searchTerm } = payload as any;
        window.dispatchEvent(
          new CustomEvent('debounce-in-collection-search', {
            detail: {
              searchTerm,
            },
          })
        );
      },
      [EVENT_NAMES.HANDLE_SHOW_RESULT]: (payload) => {
        const { target } = (payload as any) || {};
        if (target) {
          this.filterHelper.createHandler(
            () => this.filterRender.handleShowResult(target),
            EVENT_NAMES.HANDLE_SHOW_RESULT
          )();
        }
      },
    };
    Object.entries(handlers).forEach(([eventKey, handler]) => {
      this.filterHelper.on(eventKey as keyof typeof EVENT_NAMES, (payload?: EventPayload) => {
        (handler as any)({ ...payload });
      });
    });
  }

  //#region LEGACY ACTIONS
  loadVolumeBundleModule = async () => {
    if (!this.volumeBundleModule) {
      this.volumeBundleModule = await import(
        /* webpackChunkName: 'volume-bundle' */
        '../../volume-bundle'
      );
    }
    return this.volumeBundleModule;
  };
  loadVolumeBundle = (context, callbackName, ...args) => {
    if (context?.dynamicBundles?.length > 0) {
      context.dynamicBundles.forEach((bundle) => {
        if (bundle.bundle_type === 'volume') {
          this.loadVolumeBundleModule().then((module) => {
            const callback = module[callbackName];
            if (typeof callback === 'function') {
              callback(context, ...args);
            }
          });
        }
      });
    }
  };

  handleVolumeBundleClick = (context, target) => {
    if (target?.closest('.boost-sd__product-item--volume-bundle')) {
      this.loadVolumeBundle(context, 'handleClickVolumeBundle', target);
    }
  };
  //#endregion

  /**
   * Connects and initializes the filter controller with the provided configuration.
   * This is the primary entry point for setting up the filter system.
   *
   * Sets up the filter state with configuration and initializes all core functionality
   * including event listeners, UI components, and filter tree rendering.
   *
   * @param config - Configuration object containing:
   *   - document: HTMLElement - The DOM context for the filter
   *   - blockType: string - Type of block ('filter')
   *   - widgetId: string - Widget identifier
   *   - defaultParams: object - Default filter parameters
   *   - filter: object - Initial filter tree data
   *
   * @example
   * Basic usage:
   * ```typescript
   * await filterController.connect({
   *   document: document.getElementById('boost-sd-filter'),
   *   blockType: 'filter',
   *   widgetId: 'filter-widget-123',
   *   defaultParams: { page: 1, limit: 24 },
   *   filter: filterTreeData
   * });
   * ```
   *
   * @example
   * Extended implementation:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async connect(config) {
   *       // Add custom initialization logic
   *       this.initCustomAnalytics();
   *       await super.connect(config);
   *     }
   *   };
   * });
   * ```
   */
  async connect(config: Record<string, unknown>): Promise<void> {
    this.filterStore.setState({
      ...config,
    });
    this.filterService.initShortenMaps();
    await this.init();
  }

  initVolumeBundles = (context) => {
    this.loadVolumeBundle(context, 'initVolumeBundle');
  };

  /**
   * Initializes all core filter functionality.
   * Called automatically by connect() - should not be called directly in most cases.
   *
   * Orchestrates the initialization sequence including:
   * - Collection ID saving and validation
   * - Exceeded page handling
   * - Filter tree setup
   * - Core UI components (search, pagination, swatches)
   * - Event system registration
   * - Browser history handling
   * - Legacy module compatibility
   *
   * @remarks
   * This method coordinates multiple initialization subsystems in a specific order.
   * Changing the initialization order may cause issues with component dependencies.
   *
   * @example
   * Override for custom initialization sequence:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async init() {
   *       // Pre-initialization custom logic
   *       await this.loadCustomData();
   *
   *       await super.init();
   *
   *       // Post-initialization custom logic
   *       this.setupCustomFeatures();
   *     }
   *   };
   * });
   * ```
   */
  async init() {
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    this.filterService.savingCollectionId();
    await this.handleExceededPage();
    this.filterService.syncActiveFiltersToContext();

    this.handleFilterTree();
    this.handleInCollectionSearch(contextDocument);
    await this.setupCoreUI();
    this.initPageLimit();
    this.initLegacyModule();
    this.setupKeydownEvent();
    this.setupOutSideClickEvent();
    this.setupDocumentClickEvent();
    this.setupScrollToTopButton();
    this.initEventLineners();
    this.handleHistoryParams();
  }

  /**
   * Sets up browser history navigation handling
   * Listens for popstate events (back/forward navigation) and updates the filter state accordingly
   */
  handleHistoryParams(): void {
    window.addEventListener('popstate', async () => {
      // Handle browser back or forward navigation
      this.filterService.parseHistoryParams();
      this.filterService.parseCollectionPath();
      await this.handleFetchFilter();
      this.filterHelper.emit(EVENT_NAMES.FILTER_CHANGED);
      this.domHelper.scrollToTop({});
      this.filterRender.renderCollectionHeader();
    });

    // Initial setup on first load
    this.filterService.parseHistoryParams();
    this.filterRender.fillFilterTree();
    this.filterHelper.emit(EVENT_NAMES.RENDER_REFINE_BY);
  }

  /**
   * Handles scroll position restoration when returning to a product list.
   * Restores scroll to previously viewed product when navigating back from product detail page.
   *
   * This method:
   * - Disables browser's automatic scroll restoration
   * - Checks for stored product ID in session storage
   * - Validates the product is in the current collection
   * - Waits for product images to load (for auto-height layouts)
   * - Scrolls to the previously viewed product
   * - Cleans up stored values after restoration
   * - Handles iOS page cache restoration (pageshow event)
   *
   * @returns Cleanup function to remove pageshow event listener
   *
   * @remarks
   * Uses session storage keys SELECTED_PRODUCT_ID and COLLECTION_SELECTED to
   * maintain scroll position across navigation. For auto-height image layouts,
   * waits up to 1 second (20 attempts × 50ms) for aspect ratio calculation.
   *
   * @example
   * Override to customize scroll behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     handleScrollRestore() {
   *       const cleanup = super.handleScrollRestore();
   *
   *       // Add smooth scroll behavior
   *       const productId = this.filterHelper.getSessionStorage('SELECTED_PRODUCT_ID', 0);
   *       if (productId) {
   *         document.querySelector(`[data-product-id="${productId}"]`)
   *           ?.scrollIntoView({ behavior: 'smooth', block: 'center' });
   *       }
   *
   *       return cleanup;
   *     }
   *   };
   * });
   * ```
   */
  handleScrollRestore() {
    if (window.history.scrollRestoration) {
      window.history.scrollRestoration = 'manual';
    }

    const listener = (event) => {
      // Webpage is loading from cache
      if (event.persisted) {
        this.handleScrollRestore();
      }
    };

    const document = this.document;
    const productList = document.querySelector('.boost-sd__product-list');

    if (productList) {
      const productId = this.filterHelper.getSessionStorage(SELECTED_PRODUCT_ID, 0);
      const storedCollection = this.filterHelper.getSessionStorage(COLLECTION_SELECTED, '');
      const currentCollection = this.filterService.getCurrentCollection();

      // Only restore scroll if product ID exists and we're in the same collection
      if (productId && storedCollection === currentCollection) {
        const product = document.querySelector(
          `.boost-sd__product-item[data-product-id="${productId}"]`
        );

        // Has product id and not in recommendation
        if (product && !product.closest('.boost-sd__recommendation')) {
          const equalHeightWrapper = product.querySelector(
            '.boost-sd__product-image-wrapper--auto'
          ) as HTMLElement | null;
          let attempts = 0;
          const MAX_ATTEMPTS = 20;

          if (equalHeightWrapper) {
            // Only wait for aspect ratio if using auto ratio
            const intervalId = setInterval(() => {
              if (equalHeightWrapper.offsetHeight > 0 || attempts++ > MAX_ATTEMPTS) {
                this.filterHelper.scrollToProduct(product);
                clearInterval(intervalId);
              }
            }, 50);
          } else {
            // Immediate scroll for fixed ratio
            this.filterHelper.scrollToProduct(product);
          }
        }
      }

      // Clear stored values after restoration attempt or when collections don't match
      if (productId) {
        this.filterHelper.setSessionStorage(SELECTED_PRODUCT_ID, null);
        this.filterHelper.setSessionStorage(SELECTED_PRODUCT_PAGE, null);
        this.filterHelper.setSessionStorage(COLLECTION_SELECTED, null);
      }
    }

    // Handle IOS Scroll Restoration when page is loaded from cache
    window.addEventListener('pageshow', listener);

    return () => {
      window.removeEventListener('pageshow', listener);
    };
  }

  setUniformProductImageHeight = () => {
    const templateSettings = this.appService.templateSettings;
    const generalSettings = this.appService.TAEAppConfig.generalSettings;
    const mobileItemsPerRow =
      templateSettings?.themeSettings?.productList?.productsPerRowOnMobile || 2;
    const desktopItemsPerRow =
      templateSettings?.themeSettings?.productList?.productsPerRowOnDesktop || 3;
    const mobileBreakPoint = (generalSettings as any)?.breakpointmobile || 575;
    const tabletPortraitBreakPoint = (generalSettings as any)?.breakpointtabletportraitmax || 991;
    const tabletPortraitItemsPerRow = 3;
    /**
     *   The code appears to adjust the aspect ratio of product image wrappers based on the natural dimensions of the images. It does this by:
     *   1. Querying the product image wrappers and items.
     *   2. Determining the number of items per row based on the screen width and theme settings.
     *   3. Iterating through the product items in chunks of numberProductItemPerRow and setting the aspect ratio of each wrapper based on the maximum ratio found in that chunk.
     *   The code also handles cases where images may not have loaded yet by using the onload and onerror events.
     */

    const adjustProductImageHeights = () => {
      const document = this.document;
      const productImageWrappers = document.querySelectorAll(
        '.boost-sd__product-image-wrapper--auto'
      );

      if (productImageWrappers.length === 0) {
        return;
      }

      const productItems = Array.from(document.querySelectorAll('.boost-sd__product-item'));

      let numberProductItemPerRow = mobileItemsPerRow;

      if (window.innerWidth > tabletPortraitBreakPoint) {
        numberProductItemPerRow = desktopItemsPerRow;
      } else if (window.innerWidth > mobileBreakPoint) {
        numberProductItemPerRow = tabletPortraitItemsPerRow;
      }

      const setAspectRatioForRow = (itemsInRow) => {
        let maxRatio = 0;

        const updateDimensions = (img) => {
          const { naturalHeight, naturalWidth } = img;

          const ratio = naturalHeight / naturalWidth;
          if (ratio > maxRatio) {
            maxRatio = ratio;
          }

          itemsInRow.forEach((item) => {
            const wrapper = item.querySelector('.boost-sd__product-image-wrapper--auto');
            if (wrapper) {
              wrapper.style.aspectRatio = `1/${maxRatio}`;
            }
          });
        };

        itemsInRow.forEach((item) => {
          const img = item.querySelector('.boost-sd__product-image-wrapper--auto img');
          if (img) {
            if (img.complete) {
              updateDimensions(img);
            } else {
              img.onload = () => updateDimensions(img);
              img.onerror = () => {
                console.error('Failed to load image:', img.src);
              };
            }
          }
        });
      };

      for (let i = 0; i < productItems.length; i += numberProductItemPerRow) {
        const itemsInRow = productItems.slice(i, i + numberProductItemPerRow);
        setAspectRatioForRow(itemsInRow);
      }
    };

    // Adjust heights initially and on resize with debounce
    const debouncedAdjustProductImageHeights = this.filterHelper.debounce(
      adjustProductImageHeights,
      200
    );

    window.addEventListener('resize', debouncedAdjustProductImageHeights);

    // Run the function once the DOM is fully loaded
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', adjustProductImageHeights);
    } else {
      adjustProductImageHeights();
    }
  };

  /**
   * Handles responsive behavior for product list layout.
   * Adjusts product grid columns based on viewport size and view mode (grid/list).
   *
   * This method:
   * - Listens for window resize events
   * - Detects current view mode (grid or list)
   * - Applies appropriate column classes for mobile/desktop
   * - Handles grid column count based on theme settings
   * - Manages list view layout switching
   *
   * @remarks
   * Uses theme settings for productsPerRowOnDesktop and productsPerRowOnMobile
   * to determine grid columns. The isMobile() check determines when to switch
   * between mobile and desktop layouts.
   *
   * @example
   * Override to add custom responsive breakpoints:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     handleProductListResponsive() {
   *       super.handleProductListResponsive();
   *
   *       // Add tablet-specific layout
   *       window.addEventListener('resize', () => {
   *         const productList = this.document.querySelector('.boost-sd__product-list');
   *         if (window.innerWidth > 768 && window.innerWidth <= 1024) {
   *           productList?.classList.add('boost-sd__product-list-tablet-3-col');
   *         }
   *       });
   *     }
   *   };
   * });
   * ```
   */
  handleProductListResponsive() {
    const document = this.document;
    const productList = document.querySelector('.boost-sd__product-list');
    const productListListColumn = document.querySelector('.boost-sd__product-list-list-col');

    if (!productList) {
      return;
    }

    window.addEventListener('resize', () => {
      const document = this.document;
      const viewAsIconGridActive = document.querySelector('.boost-sd__view-as-icon--active');
      const viewAsIconGrid = document.querySelector(
        '.boost-sd__view-as-icon--grid'
      ) as HTMLElement | null;

      const { productsPerRowOnDesktop, productsPerRowOnMobile } = this.appService.templateSettings
        ?.themeSettings?.productList || {
        productsPerRowOnMobile: 2,
        productsPerRowOnDesktop: 3,
      };
      const { listType } = this.appService.TAEAppConfig?.templateSettings?.themeSettings
        ?.additionalElements?.toolbar?.elements?.viewAs || { listType: 'grid/list' };
      const isMobileSize = this.filterHelper.isMobile();
      const gridValue = viewAsIconGridActive?.getAttribute('data-value');

      const productListGridDesktop = 'boost-sd__product-list-' + gridValue + '-col';
      const productListGridMobile =
        'boost-sd__product-list-grid--' + productsPerRowOnMobile + '-col';

      if (isMobileSize && !productListListColumn) {
        productList.classList.remove(productListGridDesktop);
        productList.classList.add(productListGridMobile);
        viewAsIconGrid?.setAttribute('data-value', 'grid--' + productsPerRowOnMobile);
      }

      if (!isMobileSize && !productListListColumn) {
        productList.classList.remove(productListGridMobile);
        productList.classList.add(productListGridDesktop);
        if (listType === 'grid/list') {
          viewAsIconGrid?.setAttribute('data-value', 'grid--' + productsPerRowOnDesktop);
        } else {
          if (gridValue) {
            viewAsIconGrid?.setAttribute('data-value', gridValue);
          }
        }
      }
    });
  }

  /**
   * Initializes currency formatting for price filter options.
   * Sets the money format value on price and variant price filter options.
   *
   * Determines whether to use money format with currency codes (e.g., "$10.00 USD")
   * or without (e.g., "$10.00") based on theme settings, then applies the formatted
   * value to price-type filter options.
   *
   * @remarks
   * This method prepares price filter options with the correct money format before
   * rendering. The format is stripped of HTML tags to ensure clean display in filter UI.
   *
   * @example
   * Override to add custom price formatting per filter option:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     initCurrencyForFilterPrice() {
   *       super.initCurrencyForFilterPrice();
   *
   *       const filter = this.filterStore.state.value.filter;
   *       filter?.options?.forEach(option => {
   *         if (option.filterType === 'price') {
   *           // Add custom currency symbol for specific markets
   *           if (this.isEUMarket()) {
   *             option.moneyFormatValue = '€';
   *           }
   *         }
   *       });
   *     }
   *   };
   * });
   * ```
   */
  initCurrencyForFilterPrice() {
    const filter = this.filterStore.state.value.filter || {};
    const options = filter?.options;
    const { showCurrencyCodes } =
      this.appService.TAEAppConfig.templateSettings?.themeSettings?.productItems?.productInfo
        ?.elements?.price || {};
    const moneyFormat = showCurrencyCodes
      ? this.appService.TAEAppConfig.shop?.money_format_with_currency
      : this.appService.TAEAppConfig?.shop?.money_format;

    if (!moneyFormat) return;

    if (options?.length > 0) {
      options.forEach((option) => {
        if (['price', 'variants_price'].includes(option.filterType)) {
          option.moneyFormatValue = this.filterHelper.stripHtml(moneyFormat);
        }
      });
    }
  }

  /**
   * Sets up and initializes all core UI components for the filter system.
   * Handles both initial render and post-filter-update UI synchronization.
   *
   * Initializes:
   * - SEO meta tags for search engines
   * - Currency formatting for price filters
   * - Filter option collapse states
   * - Product list responsive behavior
   * - Product price display and transformations
   * - Product swatches and variant selection
   * - Image height uniformity
   * - Tooltips and animations
   * - Scroll restoration
   * - Bulk quantity controls (if enabled)
   * - Search result tabs (for search pages)
   * - Infinite scroll pagination (if enabled)
   *
   * @remarks
   * This method uses requestIdleCallback and requestAnimationFrame for performance
   * optimization on mobile devices. On desktop, operations run synchronously.
   * Post-processes are executed differently based on device type.
   *
   * @example
   * Override to add custom UI initialization:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async setupCoreUI() {
   *       await super.setupCoreUI();
   *
   *       // Add custom UI initialization
   *       this.initCustomBadges();
   *       this.setupProductHoverEffects();
   *     }
   *   };
   * });
   * ```
   *
   * @example
   * Modify UI setup for specific shop requirements:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async setupCoreUI() {
   *       // Run base setup
   *       await super.setupCoreUI();
   *
   *       // Add shop-specific UI modifications
   *       if (this.isVipShop()) {
   *         this.addVipBadgesToProducts();
   *       }
   *     }
   *   };
   * });
   * ```
   */
  async setupCoreUI() {
    this.filterService.setupSEOMetaTags();
    this.bindUiFormatCurrencyForRangeSlider();
    this.removeUIAnimation();
    this.filterRender.initFilterOptionCollapse();
    this.initCurrencyForFilterPrice();
    // Product list
    this.handleScrollRestore();
    this.handleTooltip();
    this.transformProductPrice();
    this.handleProductSwatches();
    this.handleProductListResponsive();
    this.setUniformProductImageHeight();
    const subLayout = (
      this.appService.templateSettings?.themeSettings?.productItems?.general as any
    )?.subLayout;
    if (subLayout === 'subLayout_bulk') {
      await this.handleBulkQuantity();
    }
    if (this.filterHelper.isSearchPage()) {
      const block = this.appService.blockByType('filter');
      handleSearchResultTabs(block as any, 300);
    }

    const { paginationType = 'default' } =
      this.appService.templateSettings?.themeSettings?.additionalElements?.pagination || {};

    if (paginationType === 'infinite_scroll') {
      this.handlePaginationInfiniteScroll();
    }

    // Use setTimeout instead of requestIdleCallback for Safari compatibility
    setTimeout(() => {
      // Need Remove animate class to position fixed is working.
      const document = this.document;
      const animateNeedRemove = document.querySelectorAll(
        '.animate--slide-in, .animation--fade-in'
      );
      if (animateNeedRemove.length > 0) {
        animateNeedRemove.forEach((ele) => {
          ele.classList?.remove('animate--slide-in');
          ele.classList?.remove('animation--fade-in');
        });
      }
    });

    const postProcesses = [
      () => this.handleProductListResponsive(),
      () => this.handleProductSwatches(),
      () => this.transformProductPrice(),
      () => this.handleTooltip(),
      () => this.handleScrollRestore(),
      () => this.filterRender.initFilterOptionCollapse(),
      () => this.initCurrencyForFilterPrice(),
      () => this.setUniformProductImageHeight(),
    ];

    if (this.filterHelper.isMobile()) {
      postProcesses.forEach((postProcess) => requestAnimationFrame(postProcess));
    } else {
      postProcesses.forEach((postProcess) => postProcess());
    }
  }

  /**
   * Retrieves the list of enabled product swatches from theme settings.
   * Returns an array of enabled swatch configurations (swatches.1, swatches.2, swatches.3).
   *
   * Checks theme settings for up to 3 swatch slots and returns only those that are
   * enabled, preserving their configuration including display type, option name, etc.
   *
   * @returns Array of enabled swatch configuration objects, empty array if none enabled
   *
   * @remarks
   * Each swatch object contains configuration like isEnable, displayType, optionName,
   * and other settings defined in the theme's product info elements.
   *
   * @example
   * Override to filter or modify swatch configurations:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     getEnabledSwatches() {
   *       const swatches = super.getEnabledSwatches();
   *
   *       // Only return color swatches for this shop
   *       return swatches.filter(swatch =>
   *         swatch.optionName?.toLowerCase().includes('color')
   *       );
   *     }
   *   };
   * });
   * ```
   */
  getEnabledSwatches() {
    const productInfoElements =
      this.appService.themeSettings.productItems?.productInfo?.elements || {};

    const swatches: any[] = [];

    ['swatches.1', 'swatches.2', 'swatches.3'].forEach((swatchNumber) => {
      if (swatchNumber in productInfoElements && productInfoElements[swatchNumber].isEnable) {
        swatches.push(productInfoElements[swatchNumber]);
      }
    });

    return swatches;
  }

  /**
   * Handles mouse leave event for product swatches.
   * Resets swatch selection and restores default product images when mouse leaves the swatch area.
   *
   * Respects the global flag `window.boostSDKeepVariantOnHoverOutside` which allows
   * variant selections to persist even after hovering outside.
   *
   * @param swatch - The swatch container element
   * @param productItem - The product item element
   * @param images - Array of default product images to restore
   *
   * @remarks
   * This method provides the "reset on hover out" behavior for swatches. The global
   * flag allows shops to customize whether variants persist or reset after mouse leave.
   *
   * @example
   * Override to customize hover-out behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     handleMouseLeave(swatch, productItem, images) {
   *       // Add delay before resetting
   *       const timeoutId = setTimeout(() => {
   *         super.handleMouseLeave(swatch, productItem, images);
   *       }, 300);
   *
   *       // Store timeout to cancel if mouse re-enters quickly
   *       swatch.dataset.hoverTimeout = timeoutId;
   *     }
   *   };
   * });
   * ```
   */
  handleMouseLeave = (swatch, productItem, images) => {
    swatch?.addEventListener('mouseleave', () => {
      if ((window as any).boostSDKeepVariantOnHoverOutside) return;

      const previousLabel = swatch?.querySelector('.boost-sd__radio-label--selected');

      previousLabel?.classList.remove('boost-sd__radio-label--selected');
      this.setProductImageToDefault(productItem, images);
    });
  };

  /**
   * Resets product images to their default state.
   * Restores the original product images when a swatch selection is cleared or on mouse leave.
   *
   * Handles both single and dual image displays (for hover effects) and includes
   * optimizations to prevent flickering by checking if the current image differs
   * from the target image before updating.
   *
   * @param productItem - The product item element containing images
   * @param images - Array of default image objects with src properties [firstImage, secondImage]
   *
   * @remarks
   * Generates responsive srcset for images with widths [200, 300, 400, 500, 700]px.
   * Only updates images when the src has actually changed to avoid unnecessary reflows.
   *
   * @example
   * Override to add custom image transition effects:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     setProductImageToDefault(productItem, images) {
   *       // Add fade-out effect before changing
   *       const imgs = productItem.querySelectorAll('.boost-sd__product-image img');
   *       imgs.forEach(img => img.classList.add('fading'));
   *
   *       setTimeout(() => {
   *         super.setProductImageToDefault(productItem, images);
   *         imgs.forEach(img => img.classList.remove('fading'));
   *       }, 150);
   *     }
   *   };
   * });
   * ```
   */
  setProductImageToDefault(productItem, images) {
    if (!productItem || !images?.length) return;

    const productImages = productItem.querySelectorAll('.boost-sd__product-image img');
    if (!productImages.length) return;

    const [firstImage, secondImage] = images;
    const hasSecondImage = images.length > 1;

    const generateSrcSet = (imageUrl, widths) => {
      return widths.map((width) => `${imageUrl}?width=${width} ${width}w`).join(', ');
    };

    const setProductImage = (index, image) => {
      if (productImages[index] && image) {
        // Only set product image if the new image is not the same as the current image to avoid flickering
        if (!productImages[index].src?.includes(image.src)) {
          productImages[index].src = image.src;

          const widths = [200, 300, 400, 500, 700];
          productImages[index].srcset = generateSrcSet(image.src, widths);
        }
      }
    };

    setProductImage(0, firstImage);

    // Only set the second image if both the image element and the image data exist
    if (productImages.length > 1 && hasSecondImage) {
      setProductImage(1, secondImage);
    }
  }

  /**
   * Sets up a click outside handler for an element.
   * Executes a callback when a click occurs outside the specified root element.
   *
   * Automatically removes the event listener after the first outside click is detected,
   * preventing memory leaks and ensuring one-time use behavior.
   *
   * @param rootElm - The root element to monitor for outside clicks
   * @param callback - Function to execute when clicking outside, receives the clicked target
   * @returns Cleanup function to manually remove the event listener if needed
   *
   * @remarks
   * This utility is commonly used for closing dropdowns, modals, or popups when the
   * user clicks outside. The listener is automatically cleaned up after first trigger.
   *
   * @example
   * Override to add custom outside click logic:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     handleClickOutside(rootElm, callback) {
   *       const enhancedCallback = (target) => {
   *         // Log analytics before closing
   *         this.trackOutsideClick(rootElm.id, target);
   *         callback(target);
   *       };
   *       return super.handleClickOutside(rootElm, enhancedCallback);
   *     }
   *   };
   * });
   * ```
   */
  handleClickOutside = (rootElm, callback) => {
    const onClick = (event) => {
      if (!rootElm.contains(event.target)) {
        callback(event.target);
        document.removeEventListener('click', onClick);
      }
    };

    document.addEventListener('click', onClick);

    // Optional: Cleanup function to remove event listener
    return () => document.removeEventListener('click', onClick);
  };

  /**
   * Registers event listeners for swatch option interactions.
   * Sets up click, hover, and keyboard events for individual swatch options.
   *
   * This method handles:
   * - Click or hover events to change product variant
   * - Keyboard navigation (Enter/Space keys) for accessibility
   * - Outside click detection to persist or reset variant selection
   * - Product image updates based on variant selection
   *
   * @param event - Event type to register ('click' or 'mouseover')
   * @param productItem - The product item element containing the swatch
   * @param swatch - The swatch container element
   * @param option - The individual swatch option element (button/label)
   * @param options_with_values - Variant options configuration with values
   * @param images - Product images associated with variants
   * @param defaultImages - Default product images to restore on reset
   *
   * @remarks
   * For click events, sets up outside click handling to manage selection persistence
   * based on the global `window.boostSDKeepVariantOnHoverOutside` flag. Keyboard
   * events prevent default behavior and trigger the same handler as clicks.
   *
   * @example
   * Override to add custom swatch event tracking:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     registerSwatchOptionEvent(event, productItem, swatch, option,
   *                                options_with_values, images, defaultImages) {
   *       // Add analytics tracking before registering event
   *       option?.addEventListener(event, () => {
   *         this.trackSwatchInteraction({
   *           productId: productItem.dataset.productId,
   *           swatchValue: option.dataset.value,
   *           interactionType: event
   *         });
   *       });
   *
   *       super.registerSwatchOptionEvent(event, productItem, swatch, option,
   *                                        options_with_values, images, defaultImages);
   *     }
   *   };
   * });
   * ```
   */
  registerSwatchOptionEvent = (
    event,
    productItem,
    swatch,
    option,
    options_with_values,
    images,
    defaultImages
  ) => {
    let cleanupOnClickOutside;

    option?.addEventListener(event, () => {
      this.handleSwatchOption({
        productItem,
        swatch,
        option,
        options_with_values,
        images,
        defaultImages,
      });

      if (event === 'click') {
        if (typeof cleanupOnClickOutside === 'function') cleanupOnClickOutside();

        const callback = (target) => {
          if ((window as any).boostSDKeepVariantOnClickOutside) return;

          const previousLabel = swatch?.querySelector('.boost-sd__radio-label--selected');
          previousLabel?.classList.remove('boost-sd__radio-label--selected');

          // Set product image to default only if clicked target is not a swatch option
          if (!target?.closest('.boost-sd__radio-label')) {
            this.setProductImageToDefault(productItem, defaultImages);
          }
        };

        cleanupOnClickOutside = this.handleClickOutside(swatch, callback);
      }
    });

    // Add keyboard event listeners
    option?.addEventListener('keydown', (e) => {
      if (e.key === 'Enter' || e.key === ' ') {
        this.handleSwatchOption({
          defaultImages,
          productItem,
          swatch,
          option,
          options_with_values,
          images,
        });
      }
    });
  };

  /**
   * Initializes and manages product variant swatches on product items.
   * Handles swatch interactions, variant image updates, and hover behaviors.
   *
   * Sets up swatch functionality including:
   * - Detecting enabled swatch configurations (up to 3 swatch types)
   * - Registering click and hover event listeners on swatch options
   * - Updating product images when variants are selected
   * - Managing selected state UI
   * - Handling keyboard navigation (Enter/Space keys)
   * - Outside click handling to preserve/reset variant selection
   *
   * @remarks
   * This method checks theme settings to determine which swatches are enabled
   * (swatches.1, swatches.2, swatches.3) and only initializes enabled ones.
   * Swatch behavior differs based on whether interaction is click or hover.
   *
   * The global flag `window.boostSDKeepVariantOnHoverOutside` controls whether
   * variant selections persist when hovering outside the product item.
   *
   * @example
   * Override to customize swatch behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     handleProductSwatches() {
   *       super.handleProductSwatches();
   *
   *       // Add custom swatch analytics tracking
   *       document.querySelectorAll('.boost-sd__radio-label').forEach(swatch => {
   *         swatch.addEventListener('click', () => {
   *           this.trackSwatchSelection(swatch.dataset.value);
   *         });
   *       });
   *     }
   *   };
   * });
   * ```
   *
   * @example
   * Modify swatch image update behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     handleProductSwatches() {
   *       // Override to add zoom effect on swatch hover
   *       const originalHandler = super.handleProductSwatches.bind(this);
   *       originalHandler();
   *
   *       document.querySelectorAll('.boost-sd__product-swatch-item').forEach(swatch => {
   *         swatch.addEventListener('mouseenter', (e) => {
   *           e.currentTarget.closest('.boost-sd__product-image')?.classList.add('zoom');
   *         });
   *       });
   *     }
   *   };
   * });
   * ```
   */
  handleProductSwatches() {
    const { elements } =
      this.appService.TAEAppConfig.templateSettings?.themeSettings?.productItems?.productInfo || {};
    const swatchesEnabled =
      (elements?.['swatches.1'] as any)?.isEnable ||
      (elements?.['swatches.2'] as any)?.isEnable ||
      (elements?.['swatches.3'] as any)?.isEnable;

    if (swatchesEnabled) {
      const swatches = this.getEnabledSwatches();

      // Use this.document (virtual nextDOM context) instead of global document.
      // During filter re-render, this.document = nextDOM (detached, holds new product items).
      // Attaching listeners here ensures they travel with elements when replaceWith() fires.
      const document = this.document;

      // Get all product items of a block
      const productItems = Array.from(
        document?.querySelectorAll(
          '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
        )
      ) as HTMLElement[];

      productItems?.forEach((productItem, i) => {
        // Get all product swatches, ignore the swatch from popup selection - quick add to cart
        const productSwatches = Array.from(
          productItem?.querySelectorAll('.boost-sd__product-swatch') || []
        ).filter((swatch) => !swatch.closest('.boost-sd__popup-select-option'));

        const productImageElement = productItem?.querySelector(
          '.boost-sd__product-image img'
        ) as HTMLImageElement | null;
        const productImage = productImageElement ? productImageElement.src : null;

        const dataProduct: any =
          this.filterHelper.safeParseJSON(productItem.getAttribute('data-product') || '') || {};
        const { images, options_with_values, variantId } = dataProduct;
        productSwatches?.forEach((swatch, index) => {
          // hover | click | none
          const changeProductImageOn = swatches[index]?.changeProductImageOn || 'hover';

          if (changeProductImageOn === 'none') return;

          const event = changeProductImageOn === 'hover' ? 'mouseenter' : 'click';

          const defaultImages = variantId ? [{ src: productImage }] : images;
          if (event === 'mouseenter') {
            this.handleMouseLeave(swatch, productItem, defaultImages);
          }

          const swatchOptions = swatch?.querySelectorAll('.boost-sd__product-swatch-option');

          swatchOptions?.forEach((option) =>
            this.registerSwatchOptionEvent(
              event,
              productItem,
              swatch,
              option,
              options_with_values,
              images,
              defaultImages
            )
          );
        });

        this.applySwatchSettings(
          productItem,
          options_with_values,
          (this.appService.filterSettings as any)?.swatch_settings
        );

        this.initSwatchTooltipScrollHandler(productItem);
      });
    }
  }

  /**
   * Handles swatch option selection and updates product images.
   * Manages variant image changes and selected state when user interacts with swatches.
   *
   * This method:
   * - Identifies the variant option from the swatch type
   * - Updates product images based on variant selection
   * - Handles cases where variants have no specific images
   * - Manages selected label state (visual indicator)
   * - Falls back to default images when variant has no image
   *
   * @param params - Swatch interaction parameters
   * @param params.productItem - The product item element
   * @param params.swatch - The swatch container element
   * @param params.option - The selected swatch option element
   * @param params.options_with_values - Product variant options configuration
   * @param params.images - Product images with position mapping
   * @param params.defaultImages - Default images to restore on fallback
   *
   * @remarks
   * This method is called when users click or hover over swatches. It finds the
   * matching variant from options_with_values using the swatch type (name or original_name),
   * then updates product images based on the variant's image position.
   *
   * @example
   * Override to add custom image transition effects:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected handleSwatchOption(params) {
   *       // Add fade effect before image change
   *       const productImages = params.productItem.querySelectorAll('.boost-sd__product-image img');
   *       productImages.forEach(img => img.classList.add('fading'));
   *
   *       super.handleSwatchOption(params);
   *
   *       // Remove fade class after transition
   *       setTimeout(() => {
   *         productImages.forEach(img => img.classList.remove('fading'));
   *       }, 300);
   *     }
   *   };
   * });
   * ```
   *
   * @example
   * Add analytics tracking for swatch selections:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected handleSwatchOption(params) {
   *       // Track swatch selection
   *       const swatchValue = params.option.querySelector('.boost-sd__radio-input')?.value;
   *       this.trackSwatchSelection({
   *         productId: params.productItem.dataset.productId,
   *         variant: swatchValue
   *       });
   *
   *       super.handleSwatchOption(params);
   *     }
   *   };
   * });
   * ```
   */
  protected handleSwatchOption(params: {
    productItem: any;
    swatch: any;
    option: any;
    options_with_values: any;
    images: any;
    defaultImages?: any;
  }): void {
    const { productItem, swatch, option, options_with_values, images, defaultImages } = params;

    const swatchType = option
      .querySelector('.boost-sd__radio-input')
      ?.getAttribute('data-swatch-type');
    const variantOption = options_with_values?.find(
      (option) => option.name === swatchType || option.original_name === swatchType
    );

    const setProductImage = (position) => {
      const productImages = productItem.querySelectorAll('.boost-sd__product-image img');

      if (!productImages || productImages.length === 0) return;

      productImages.forEach((img) => {
        const currentPosition = img.classList.contains('boost-sd__product-image-img--main')
          ? position
          : position + 1;
        const matchedImage = images?.find((item) => item.position === currentPosition)?.src;

        if (matchedImage && img.src !== matchedImage) {
          img.src = matchedImage;
          img.srcset = matchedImage;
        }
      });
    };

    const setSelectedLabel = (label) => {
      // Remove previous selected
      const previousLabel = swatch?.querySelector('.boost-sd__radio-label--selected');
      previousLabel?.classList.remove('boost-sd__radio-label--selected');

      // Mark as selected
      label?.classList.add('boost-sd__radio-label--selected');
    };

    if (variantOption) {
      const radioInput = option.querySelector('.boost-sd__radio-input');
      const radioInputValue = radioInput?.getAttribute('value');
      const variantValue = variantOption?.values?.find((value) => value.title === radioInputValue);

      if (variantValue?.image) {
        setProductImage(variantValue.image);
      } else {
        // Variant option has no image, fall back to first and second of images
        this.setProductImageToDefault(productItem, defaultImages);
      }
    }

    setSelectedLabel(option.querySelector('.boost-sd__radio-label'));
  }

  /**
   * Initializes scroll handlers for swatch tooltips.
   * Attaches scroll event listeners to hide tooltips when page scrolls.
   *
   * This method:
   * - Iterates through all swatch options in the container
   * - Prevents duplicate listeners using WeakSet tracking
   * - Attaches touchstart and click events to trigger scroll handler
   * - Ensures tooltips hide when user scrolls the page
   *
   * @param container - The product item container element
   *
   * @remarks
   * Uses a WeakSet to track which labels have been processed to prevent
   * duplicate event listener registration. This is important for performance
   * and avoiding memory leaks when the method is called multiple times.
   *
   * @example
   * Override to customize tooltip scroll behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected initSwatchTooltipScrollHandler(container) {
   *       super.initSwatchTooltipScrollHandler(container);
   *
   *       // Add custom scroll threshold before hiding
   *       container.querySelectorAll('.boost-sd__tooltip-content').forEach(tooltip => {
   *         tooltip.dataset.scrollThreshold = '50';
   *       });
   *     }
   *   };
   * });
   * ```
   */
  protected initSwatchTooltipScrollHandler(container: HTMLElement): void {
    const swatchBlocks = container.querySelectorAll('.boost-sd__product-swatch');
    const processedLabels = this.filterStore.getState('processedLabels');
    swatchBlocks.forEach((swatchBlock) => {
      const options = swatchBlock.querySelectorAll('.boost-sd__product-swatch-option');
      options.forEach((option) => {
        const label = option.querySelector('.boost-sd__radio-label') as HTMLElement;
        const tooltip = option.querySelector('.boost-sd__tooltip-content') as HTMLElement;
        if (!label || !tooltip || processedLabels.has(label)) return;

        const attachTooltipScrollHandler = () => {
          this.addSwatchTooltipScrollHandler(tooltip);
        };

        label.addEventListener('touchstart', attachTooltipScrollHandler, { passive: true });
        label.addEventListener('click', attachTooltipScrollHandler);

        // Add label to processedLabels to prevent duplicate listeners
        processedLabels.add(label);
      });
    });
  }

  /**
   * Adds scroll event handler to hide tooltip when page scrolls.
   * Attaches a scroll listener that hides the tooltip and auto-removes itself.
   *
   * This method:
   * - Creates a stable handler reference for proper cleanup
   * - Hides tooltip on scroll event
   * - Auto-removes the listener after first trigger
   * - Prevents duplicate listeners on the same tooltip
   *
   * @param tooltipContent - The tooltip element to attach handler to
   *
   * @remarks
   * Uses a private property on the tooltip element (_hideOnScrollHandler) to
   * maintain a stable reference for the event handler. This allows proper
   * listener removal and prevents memory leaks.
   *
   * @example
   * Override to customize scroll hide behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected addSwatchTooltipScrollHandler(tooltipContent) {
   *       // Add fade-out animation before hiding
   *       const originalHandler = super.addSwatchTooltipScrollHandler.bind(this);
   *       if (!tooltipContent._customHideHandler) {
   *         tooltipContent._customHideHandler = () => {
   *           tooltipContent.classList.add('fade-out');
   *           setTimeout(() => {
   *             originalHandler(tooltipContent);
   *           }, 200);
   *         };
   *       }
   *       window.addEventListener('scroll', tooltipContent._customHideHandler, { passive: true });
   *     }
   *   };
   * });
   * ```
   */
  protected addSwatchTooltipScrollHandler(tooltipContent: any): void {
    if (!tooltipContent) return;

    // Use a stable handler reference to allow proper removeEventListener
    if (!tooltipContent._hideOnScrollHandler) {
      tooltipContent._hideOnScrollHandler = function hideOnScroll() {
        tooltipContent.style.display = 'none';
        window.removeEventListener('scroll', tooltipContent._hideOnScrollHandler);
      };
    }
    // Always remove before adding to avoid duplicates
    window.removeEventListener('scroll', tooltipContent._hideOnScrollHandler);
    window.addEventListener('scroll', tooltipContent._hideOnScrollHandler, { passive: true });
  }

  /**
   * Applies custom swatch settings from admin configuration to product items.
   * Updates swatch displays with custom colors, images, and text based on settings.
   *
   * This method processes swatch options and applies admin-configured settings including:
   * - Custom text labels for variant options
   * - Custom color codes (single or gradient)
   * - Custom images for swatches
   * - Mixed color and image displays
   *
   * @param container - The product item container element
   * @param options_with_values - Product variant options with values
   * @param swatch_settings - Admin-configured swatch settings object
   *
   * @remarks
   * Swatch settings are keyed by lowercase variant values. The method handles three
   * display types: text, product-image, and color-and-image. Settings can include
   * color codes (supporting gradients with multiple colors) and image URLs.
   *
   * @example
   * Override to add custom swatch validation:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected applySwatchSettings(container, options_with_values, swatch_settings) {
   *       // Validate settings before applying
   *       if (!this.validateSwatchSettings(swatch_settings)) {
   *         console.warn('Invalid swatch settings');
   *         return;
   *       }
   *       super.applySwatchSettings(container, options_with_values, swatch_settings);
   *     }
   *   };
   * });
   * ```
   *
   * @example
   * Add custom swatch transformations:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected applySwatchSettings(container, options_with_values, swatch_settings) {
   *       // Apply base settings
   *       super.applySwatchSettings(container, options_with_values, swatch_settings);
   *
   *       // Add custom border for premium colors
   *       container.querySelectorAll('.boost-sd__radio-label').forEach(label => {
   *         if (this.isPremiumColor(label.style.backgroundColor)) {
   *           label.classList.add('premium-swatch');
   *         }
   *       });
   *     }
   *   };
   * });
   * ```
   */
  protected applySwatchSettings(
    container: HTMLElement,
    options_with_values: any,
    swatch_settings: Record<string, any>
  ): void {
    if (!swatch_settings || typeof swatch_settings !== 'object') return;
    const swatchBlocks = container.querySelectorAll('.boost-sd__product-swatch');

    swatchBlocks.forEach((swatchBlock) => {
      const optionNodes = swatchBlock.querySelectorAll('.boost-sd__product-swatch-option');

      optionNodes.forEach((processOptionNode) => {
        const input = processOptionNode.querySelector(
          'input.boost-sd__radio-input'
        ) as HTMLInputElement;
        const label = processOptionNode.querySelector(
          'label.boost-sd__radio-label'
        ) as HTMLLabelElement;
        const tooltip = processOptionNode.querySelector(
          '.boost-sd__tooltip-content'
        ) as HTMLElement;
        const swatchBy = processOptionNode.getAttribute('data-swatch-by');
        if (!input || !label) return;

        const rawValue = input.value || '';
        const value = rawValue.toLowerCase();
        const swatchObj = swatch_settings[value] || swatch_settings[`${SWATCH_PREFIX}${value}`];
        if (!swatchObj) return;

        // Handler logic
        if (swatchBy === SWATCH_OPTION_TYPE.TEXT) {
          this.handleTextSwatch(label, tooltip, swatchObj);
        } else if (swatchBy === SWATCH_OPTION_TYPE.PRODUCT_IMAGE) {
          this.handleProductImageSwatch(label, swatchObj);
        } else if (swatchBy === SWATCH_OPTION_TYPE.COLOR_IMAGE) {
          this.handleColorImageSwatch(label, swatchObj);
        }
      });
    });
  }

  /**
   * Handles text-based swatch display.
   * Updates label and tooltip text content with custom text from settings.
   *
   * @param label - The swatch label element
   * @param tooltip - The tooltip element (optional)
   * @param swatchObj - Swatch settings object containing text property
   *
   * @remarks
   * Text swatches display custom text instead of the original variant value.
   * This is useful for abbreviations, translations, or custom naming.
   *
   * @example
   * Override to add text transformations:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected handleTextSwatch(label, tooltip, swatchObj) {
   *       super.handleTextSwatch(label, tooltip, swatchObj);
   *       // Uppercase all text swatches
   *       if (label.textContent) {
   *         label.textContent = label.textContent.toUpperCase();
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected handleTextSwatch(
    label: HTMLElement,
    tooltip: HTMLElement | null,
    swatchObj: { text?: string }
  ): void {
    if (swatchObj.text) {
      label.textContent = swatchObj.text;
      if (tooltip) {
        tooltip.textContent = swatchObj.text;
      }
    }
  }

  /**
   * Handles product image-based swatch display.
   * Applies custom images or colors to product image swatches.
   *
   * @param label - The swatch label element
   * @param swatchObj - Swatch settings object containing imageUrl or colorCodes
   *
   * @remarks
   * Product image swatches prioritize custom images over colors. If no background
   * image exists, applies either the custom image or color codes from settings.
   *
   * @example
   * Override to add image loading optimization:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected handleProductImageSwatch(label, swatchObj) {
   *       if (swatchObj.imageUrl) {
   *         // Preload image for better UX
   *         const img = new Image();
   *         img.onload = () => {
   *           super.handleProductImageSwatch(label, swatchObj);
   *         };
   *         img.src = swatchObj.imageUrl;
   *       } else {
   *         super.handleProductImageSwatch(label, swatchObj);
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected handleProductImageSwatch(
    label: HTMLElement,
    swatchObj: { imageUrl?: string; colorCodes?: string[] }
  ): void {
    if (this.isBackgroundImageEmpty(label) && swatchObj.imageUrl) {
      label.style.backgroundImage = `url('${swatchObj.imageUrl}')`;
    } else if (
      this.isBackgroundImageEmpty(label) &&
      Array.isArray(swatchObj.colorCodes) &&
      swatchObj.colorCodes.length
    ) {
      this.applyColorToSwatch(label, swatchObj.colorCodes);
    }
  }

  /**
   * Handles color and image combined swatch display.
   * Applies custom images with optional color backgrounds or color gradients.
   *
   * @param label - The swatch label element
   * @param swatchObj - Swatch settings object containing imageUrl and/or colorCodes
   *
   * @remarks
   * This swatch type supports mixed display of images and colors. Images take
   * precedence, with the first color code used as background if both are provided.
   * Falls back to color-only display if no image is specified.
   *
   * @example
   * Override to add pattern overlays:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected handleColorImageSwatch(label, swatchObj) {
   *       super.handleColorImageSwatch(label, swatchObj);
   *       // Add pattern overlay for specific materials
   *       if (this.hasTexturePattern(swatchObj)) {
   *         label.style.backgroundBlendMode = 'multiply';
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected handleColorImageSwatch(
    label: HTMLElement,
    swatchObj: { imageUrl?: string; colorCodes?: string[] }
  ): void {
    const hasColorCodes = Array.isArray(swatchObj.colorCodes) && swatchObj.colorCodes.length > 0;

    if (swatchObj.imageUrl) {
      label.style.backgroundImage = `url('${swatchObj.imageUrl}')`;

      if (hasColorCodes && swatchObj.colorCodes) {
        label.style.backgroundColor = swatchObj.colorCodes[0];
      }
    } else if (hasColorCodes && swatchObj.colorCodes) {
      this.applyColorToSwatch(label, swatchObj.colorCodes);
    }
  }

  /**
   * Applies color styling to a swatch element.
   * Handles single colors and two-color gradients.
   *
   * @param label - The swatch label element to style
   * @param colorCodes - Array of color codes (1 or 2 colors)
   *
   * @remarks
   * - Single color: Applied as solid background color
   * - Two colors: Applied as diagonal gradient (top-left split)
   * - Empty/invalid: Clears all background styling
   *
   * @example
   * Override to add custom gradient angles:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected applyColorToSwatch(label, colorCodes) {
   *       if (Array.isArray(colorCodes) && colorCodes.length > 1) {
   *         // Use horizontal gradient instead of diagonal
   *         const [color1, color2] = colorCodes;
   *         label.style.backgroundImage = `linear-gradient(to right, ${color1} 50%, ${color2} 50%)`;
   *         label.style.backgroundColor = '';
   *       } else {
   *         super.applyColorToSwatch(label, colorCodes);
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected applyColorToSwatch(label: HTMLElement, colorCodes: string[]): void {
    if (Array.isArray(colorCodes)) {
      const [color1, color2] = colorCodes;
      if (colorCodes.length > 1 && color1 && color2) {
        label.style.backgroundImage = `linear-gradient(to top left, ${color2} 50%, ${color1} 50%)`;
        label.style.backgroundColor = '';
      } else if (colorCodes.length === 1 && color1) {
        label.style.backgroundColor = color1;
        label.style.backgroundImage = '';
      } else {
        label.style.backgroundImage = '';
        label.style.backgroundColor = '';
      }
    }
  }

  /**
   * Checks if an element has an empty background image.
   * Verifies both inline and computed background image styles.
   *
   * @param label - The element to check
   * @returns True if background image is empty or 'none', false otherwise
   *
   * @remarks
   * This method checks both inline styles and computed styles to determine if a
   * background image is truly absent. Used to determine if custom images should
   * be applied to swatch elements.
   *
   * @example
   * Override to add additional checks:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     protected isBackgroundImageEmpty(label) {
   *       // Also check for data attributes indicating image state
   *       if (label.dataset.hasCustomImage === 'true') {
   *         return false;
   *       }
   *       return super.isBackgroundImageEmpty(label);
   *     }
   *   };
   * });
   * ```
   */
  protected isBackgroundImageEmpty(label: HTMLElement): boolean {
    return (
      !(label.style.backgroundImage && label.style.backgroundImage !== 'none') &&
      (!window.getComputedStyle(label).backgroundImage ||
        window.getComputedStyle(label).backgroundImage === 'none')
    );
  }

  /**
   * Initializes pagination limit settings from local storage or admin defaults.
   * Handles the products-per-page limit for the filter and manages persistence.
   *
   * This method:
   * - Retrieves saved limit from local storage or uses admin default
   * - Sets the limit in filter context for API calls
   * - Saves both admin default and current limit for comparison
   * - Initializes pagination session with current page
   *
   * @remarks
   * The method prioritizes saved user preferences over admin defaults, allowing
   * users to maintain their preferred products-per-page setting across sessions.
   * The admin default is stored separately for comparison and reset capabilities.
   *
   * @example
   * Override to apply shop-specific limit logic:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     initPageLimit() {
   *       super.initPageLimit();
   *
   *       // Apply VIP customer higher limits
   *       if (this.isVipCustomer()) {
   *         const defaultParams = this.filterStore.state.value.defaultParams;
   *         defaultParams.limit = 50; // VIP customers see more products
   *         this.filterHelper.setLocalStorage('LIMIT', '50');
   *       }
   *     }
   *   };
   * });
   * ```
   */
  initPageLimit() {
    //TODO: move to service
    const defaultParams = this.filterStore.state.value.defaultParams || {};
    const page = Number(defaultParams?.page) || 1;
    const productPerPage = this.appService.themeSettings?.productList?.productsPerPage || 16;
    const savedLimit = this.filterHelper.getLocalStorage(LIMIT);

    // Use saved limit if available and valid, otherwise use admin default
    const currentLimit =
      savedLimit && !isNaN(Number(savedLimit)) ? Number(savedLimit) : productPerPage;

    // Set the limit in context for API calls
    defaultParams.limit = currentLimit;

    this.filterHelper.setPaginationSession(page);

    // Save admin default for comparison
    this.filterHelper.setLocalStorage(LIMIT_SETTING, `${productPerPage}`);

    // Save current limit for persistence
    this.filterHelper.setLocalStorage(LIMIT, `${currentLimit}`);
  }

  /**
   * Initializes legacy modules for backward compatibility.
   * Sets up dynamic bundles and volume bundles using the legacy API.
   *
   * This method bridges the gap between the new filter controller architecture
   * and older bundle implementations that haven't been fully refactored yet.
   *
   * @remarks
   * This is maintained for backward compatibility with shops using legacy bundle
   * configurations. New implementations should use the modernized bundle modules.
   *
   * @example
   * Override to skip legacy modules for shops that don't need them:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     initLegacyModule() {
   *       // Skip legacy initialization for modern shops
   *       if (this.appService.TAEAppConfig.useLegacyBundles !== true) {
   *         return;
   *       }
   *       super.initLegacyModule();
   *     }
   *   };
   * });
   * ```
   */
  initLegacyModule() {
    const context = this.appService.blockByType('filter');
    initDynamicBundle(context as any);
    this.initVolumeBundles(context);
  }

  /**
   * Sets up keyboard event listeners for filter interactions.
   * Handles keyboard navigation for sort controls, view options, limit lists, and bundles.
   *
   * Supports keyboard accessibility by responding to standard keys (Enter, Space, Arrow keys).
   * Stores the key code in filter state for use by other handlers.
   *
   * @remarks
   * This method enables keyboard-only navigation through filter controls, ensuring
   * WCAG compliance for accessibility. The keydown events are captured on the filter
   * document context to handle all keyboard interactions within the filter scope.
   *
   * @example
   * Override to add custom keyboard shortcuts:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async setupKeydownEvent() {
   *       await super.setupKeydownEvent();
   *
   *       // Add custom keyboard shortcut (e.g., 'F' to focus filter)
   *       document.addEventListener('keydown', (e) => {
   *         if (e.key === 'f' && e.ctrlKey) {
   *           this.focusFilterInput();
   *         }
   *       });
   *     }
   *   };
   * });
   * ```
   */
  async setupKeydownEvent() {
    // General context.document keydown listener
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    contextDocument?.addEventListener('keydown', (event: KeyboardEvent) => {
      const target = event.target;
      if (!target || !(target instanceof HTMLElement)) return;
      const currentButton = target.closest('button');
      const charCode = event.key;
      if (currentButton && currentButton.id) {
        this.filterStore.setState({
          [FILTER_TREE_STATE_KEY.KEY_CODE]: charCode,
        });
      }

      if (KEYS.includes(event.key)) {
        // handle keydown on sortby
        if (
          target.classList &&
          [...target.classList].some((className) => className.startsWith('boost-sd__sorting-'))
        ) {
          // context.handler(handleSort, 'handleSort')(context, target);
        }

        // handle keydown on viewas
        if (
          target.classList.contains('boost-sd__view-as-icon') ||
          target.closest('.boost-sd__view-as-icon')
        ) {
          // context.handler(handleViewAsClick, 'handleViewAsClick')(context, target);
        }

        // handle keydown on limit-list
        if (target.classList.contains('boost-sd__show-limit-list-button')) {
          // context.handler(handleToggleShowLimitList, 'handleToggleShowLimitList')(context, target);
        }
        if (target.classList.contains('boost-sd__show-limit-list-option')) {
          // context.handler(handleLimitListOptionClick, 'handleLimitListOptionClick')(
          //   context,
          //   target
          // );
        }

        //handle keydown on dynamic bundle as product
        const context = this.appService.blockByType('filter');
        if (target?.closest('.boost-sd__product-item--embedded-bundle')) {
          handleClickEmbeddedBundle(context as any, target);
        }

        this.handleVolumeBundleClick(context, target);
      }
    });
  }

  /**
   * Sets up global click event listeners to handle clicks outside filter components.
   * Manages closing behaviors for dropdowns, filter options, and popups when clicking outside.
   *
   * Handles:
   * - Closing horizontal filter options when clicking outside
   * - Closing sort dropdowns
   * - Closing limit list dropdowns
   * - Closing mini popups
   *
   * @remarks
   * This method uses event delegation on the document level to efficiently handle
   * outside clicks for multiple filter components. For horizontal filters, it applies
   * pending selections or closes the option when clicking outside.
   *
   * @example
   * Override to add custom outside click behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async setupOutSideClickEvent() {
   *       await super.setupOutSideClickEvent();
   *
   *       // Add custom outside click handler for custom components
   *       document.addEventListener('click', (e) => {
   *         if (!e.target.closest('.custom-filter-modal')) {
   *           this.closeCustomModal();
   *         }
   *       });
   *     }
   *   };
   * });
   * ```
   */
  async setupOutSideClickEvent() {
    document.addEventListener('click', (event: Event) => {
      if (!event) return;
      const target = event.target;
      if (!target || !(target instanceof HTMLElement)) return;

      const { filterLayout } = this.appService.filterSettings;

      //click outside filter option with horizontal style
      if (filterLayout === 'horizontal') {
        // click outside and current filter option is opening
        if (
          !target.closest('.boost-sd__filter-option') &&
          this.document.querySelector('.boost-sd__filter-option-title--opening')
        ) {
          const filterOptionItemSelecting =
            this.filterStore.state.value.filterOptionItemSelecting || {};

          if (Object.keys(filterOptionItemSelecting).length > 0) {
            this.filterRender.applyFilterOption({});
          } else {
            this.filterRender.closeFilterOptionHorizontalOpening();
          }
        }
      }
      this.filterHelper.emit(EVENT_NAMES.HANDLE_OUTSIDE_SORT_CLICK, { target });
      this.filterHelper.emit(EVENT_NAMES.HANDLE_OUTSIDE_LIMIT_LIST_CLICK, { target });
      this.filterHelper.emit(EVENT_NAMES.HANDLE_OUTSIDE_MINI_POPUP_CLICK, { target });
    });
  }

  /**
   * Sets up click event listeners on the filter document context.
   * Delegates all click handling within the filter scope to the FilterHandler.
   *
   * This method uses event delegation to efficiently handle all clicks within the
   * filter context, including filter options, buttons, links, and interactive elements.
   *
   * @remarks
   * The actual click handling logic is delegated to `FilterHandler.handleCoreClick()`,
   * which processes different click targets and triggers appropriate actions.
   * This separation of concerns keeps the controller focused on setup while the
   * handler manages the event processing logic.
   *
   * @example
   * Override to add pre-processing before core click handling:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async setupDocumentClickEvent() {
   *       const contextDocument = this.filterStore.state.value.document;
   *       contextDocument?.addEventListener('click', (event) => {
   *         // Add custom analytics tracking
   *         this.trackFilterClick(event.target);
   *
   *         // Call original handler
   *         this.filterHandler.handleCoreClick(event);
   *       });
   *     }
   *   };
   * });
   * ```
   */
  async setupDocumentClickEvent() {
    // General context.document filter listener
    const contextDocument = this.filterStore.state.value.document as HTMLElement;
    contextDocument?.addEventListener('click', (event: Event) => {
      this.filterHandler.handleCoreClick(event);
    });
  }

  /**
   * Sets up the scroll-to-top button behavior.
   * Shows or hides the button based on scroll position with a 300px threshold.
   *
   * The button becomes visible when the user scrolls down more than 300 pixels
   * and is hidden when scrolling back to the top. Uses debouncing (200ms) for
   * performance optimization.
   *
   * @remarks
   * The scroll listener is debounced to avoid excessive DOM manipulation during
   * scroll events. The 300px threshold provides a good balance between visibility
   * and user experience.
   *
   * @example
   * Override to customize scroll threshold or button behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     setupScrollToTopButton() {
   *       const customThreshold = 500; // Custom threshold
   *       const debounced = this.filterHelper.debounce(() => {
   *         const btn = this.filterStore.getState('document')
   *           .querySelector('.boost-sd__scroll-to-top');
   *         if (btn) {
   *           // Add fade animation
   *           btn.style.opacity = window.scrollY > customThreshold ? '1' : '0';
   *           btn.style.display = window.scrollY > customThreshold ? 'block' : 'none';
   *         }
   *       }, 200);
   *       window.addEventListener('scroll', debounced);
   *     }
   *   };
   * });
   * ```
   */
  setupScrollToTopButton() {
    const debounceScrollToTopButton = this.filterHelper.debounce(() => {
      const btnScrollToTop = this.filterStore
        .getState('document')
        .querySelector('.boost-sd__scroll-to-top') as HTMLElement | null;
      if (btnScrollToTop) {
        btnScrollToTop.style.display = window.scrollY > 300 ? 'block' : 'none';
      }
    }, 200);

    window.addEventListener('scroll', debounceScrollToTopButton);
  }

  /**
   * Binds currency formatting to range slider UI elements.
   * Updates the currency unit display for price range sliders based on shop settings.
   *
   * Queries all range slider unit elements and replaces their content with the
   * formatted money symbol (e.g., "$", "€", "£", "₫") from the shop's money format.
   * Respects the showCurrencyCodes setting to use the appropriate format.
   *
   * @param container - Optional container element to query within. If not provided, uses filterStore document.
   *
   * @remarks
   * This method delegates to FilterRender.formatRangeSliderCurrency() to avoid code duplication.
   * It ensures price range sliders display the correct currency symbol that matches the shop's
   * configuration and showCurrencyCodes setting.
   *
   * @example
   * Override to add custom currency formatting:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     bindUiFormatCurrencyForRangeSlider() {
   *       super.bindUiFormatCurrencyForRangeSlider();
   *
   *       // Add custom text for multi-currency shops
   *       const units = document.querySelectorAll('.boost-sd__filter-option-range-slider-unit');
   *       units?.forEach(unit => {
   *         unit.setAttribute('data-currency', this.getCurrentCurrency());
   *       });
   *     }
   *   };
   * });
   * ```
   */
  bindUiFormatCurrencyForRangeSlider(container?: HTMLElement): void {
    // Delegate to FilterRender to avoid code duplication
    const queryContext = (container || this.filterStore.getState('document')) as HTMLElement;
    if (queryContext) {
      this.filterRender.formatRangeSliderCurrency(queryContext);
    }
  }

  /**
   * Removes CSS animation classes from elements to enable proper positioning.
   * Strips 'animate--slide-in' and 'animation--fade-in' classes that may interfere
   * with fixed positioning or filter functionality.
   *
   * This method is necessary because some themes apply animation classes that can
   * cause layout issues with fixed-position filter elements or sticky behaviors.
   *
   * @remarks
   * Called during core UI setup to ensure filter elements can be properly positioned
   * without interference from theme animation classes. Particularly important for
   * sticky filter trees and fixed-position UI elements.
   *
   * @example
   * Override to remove additional animation classes:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     removeUIAnimation() {
   *       super.removeUIAnimation();
   *
   *       // Remove custom theme animations
   *       const customAnimations = this.document.querySelectorAll('.theme-fade, .theme-slide');
   *       customAnimations.forEach(ele => {
   *         ele.classList.remove('theme-fade', 'theme-slide');
   *       });
   *     }
   *   };
   * });
   * ```
   */
  removeUIAnimation() {
    // Need Remove animate class to position fixed is working.
    const document = this.document;
    const animateNeedRemove = document.querySelectorAll('.animate--slide-in, .animation--fade-in');
    if (animateNeedRemove.length > 0) {
      animateNeedRemove.forEach((ele) => {
        ele.classList?.remove('animate--slide-in');
        ele.classList?.remove('animation--fade-in');
      });
    }
  }

  /**
   * Transforms HTML response to apply filter tree styles based on current state
   * Handles mobile/desktop toggle button states and layout styles
   *
   * @param html - Original HTML string from API response
   * @returns Transformed HTML string with applied styles
   */
  transformFilterTreeHTML(html: string): string {
    const { filterLayout, filterTreeVerticalStyle, filterTreeMobileStyle } =
      this.appService.filterSettings;

    const filterTreeDesktopOpening =
      this.filterStore.state.value[FILTER_TREE_STATE_KEY.DESKTOP_OPENING];

    const isMobileSize = this.filterHelper.isMobile(
      parseInt(
        (this.appService.TAEAppConfig.generalSettings as { breakpointtabletportraitmax?: string })
          ?.breakpointtabletportraitmax || '768',
        10
      )
    );

    let transformedHtml = html;

    // Update mobile toggle button active state
    if (isMobileSize && filterTreeMobileStyle === 'style1') {
      const filterTreeMobileWrapper = this.filterStore
        .getState('document')
        .querySelector('.boost-sd__filter-tree-mobile-wrapper') as HTMLElement;
      if (filterTreeMobileWrapper?.style?.display !== 'none') {
        transformedHtml = transformedHtml.replace(
          'class="boost-sd__filter-tree-toggle-button"',
          'class="boost-sd__filter-tree-toggle-button boost-sd__filter-tree-toggle-button--active"'
        );
      }
    }

    // Update desktop vertical toggle button active state
    if (
      !isMobileSize &&
      filterTreeVerticalStyle !== 'style-default' &&
      filterLayout === 'vertical' &&
      filterTreeDesktopOpening
    ) {
      transformedHtml = transformedHtml.replace(
        'class="boost-sd__filter-tree-toggle-button boost-sd__filter-tree-toggle-button--expand"',
        'class="boost-sd__filter-tree-toggle-button boost-sd__filter-tree-toggle-button--expand boost-sd__filter-tree-toggle-button--active"'
      );
    }

    // Handle off-canvas style
    if (filterTreeVerticalStyle === 'style-off-canvas' && filterTreeDesktopOpening) {
      transformedHtml = transformedHtml.replace(
        'class="boost-sd__filter-tree-vertical-sticky-overlay boost-sd__filter-tree-vertical--hidden"',
        'class="boost-sd__filter-tree-vertical-sticky-overlay"'
      );
    }

    // Handle expand style
    if (filterTreeVerticalStyle === 'style-expand' && filterTreeDesktopOpening) {
      transformedHtml = transformedHtml.replace(
        'class="boost-sd__filter-tree-vertical  boost-sd__filter-tree-vertical--hidden',
        'class="boost-sd__filter-tree-vertical'
      );
    }

    // Reset desktop opening for horizontal expand
    if (
      filterLayout === 'horizontal' &&
      this.appService.filterSettings.filterTreeHorizontalStyle === 'style-expand'
    ) {
      this.filterStore.setState({ filterTreeDesktopOpening: false });
    }

    return transformedHtml;
  }

  /**
   * Updates DOM with filter results
   * Handles different update behaviors: replace, append (more), or prepend (previous)
   *
   * @param domId - Target DOM element ID
   * @param html - HTML content to render
   * @param behavior - Update behavior type
   */
  updateFilterDOM(
    domId: string,
    html: string,
    behavior: 'replace' | 'more' | 'previous' = 'replace'
  ): void {
    const isAppendToProductList = behavior === 'more' || behavior === 'previous';

    if (isAppendToProductList) {
      this.handleProductListAppend(html, behavior);
    } else {
      this.handleFullPageReplace(domId, html);
    }

    // Handle pagination session
    const page = this.filterHelper.getQueryParamByKey('page');
    if (page) {
      this.filterHelper.setPaginationSession(page);
    }

    // Update infinite scroll loading state
    this.updateInfiniteScrollState();

    // Manage body scroll class
    this.manageBodyScrollClass();

    // Hide loading icon
    this.hideLoadingIcon();
  }

  /**
   * Shows the filter loading icon
   */
  showLoadingIcon(): void {
    const loadingIcon = document.getElementById('boost-sd-loading-icon-filter');
    if (loadingIcon) {
      loadingIcon.classList.remove('boost-sd__g-hide');
    }
  }

  /**
   * Hides the filter loading icon
   */
  protected hideLoadingIcon(): void {
    const loadingIcon = document.getElementById('boost-sd-loading-icon-filter');
    if (loadingIcon) {
      loadingIcon.classList.add('boost-sd__g-hide');
    }
  }

  /**
   * Handles appending/prepending product items to the product list
   *
   * @param html - HTML content to append
   * @param behavior - 'more' for append, 'previous' for prepend
   */
  protected handleProductListAppend(html: string, behavior: 'more' | 'previous'): void {
    const productList = document.querySelector('.boost-sd__product-list');
    if (!productList) return;

    // Remove lazy loading attribute
    const cleanedHtml = html.replace(/loading="lazy"/g, '');

    // Extract inner product list content
    const outerDiv = document.createElement('div');
    outerDiv.innerHTML = cleanedHtml;
    const innerContent = outerDiv.querySelector('.boost-sd__product-list');
    const modifiedHtml = innerContent?.innerHTML;

    if (!modifiedHtml) return;

    if (behavior === 'previous') {
      const firstProductItem = productList.querySelector('.boost-sd__product-item');
      firstProductItem?.insertAdjacentHTML('beforebegin', modifiedHtml);
    } else if (behavior === 'more') {
      const lastProductItem = productList.querySelector('.boost-sd__product-item:last-child');
      lastProductItem?.insertAdjacentHTML('afterend', modifiedHtml);
    }

    // Transform product prices
    this.transformProductPrice();
  }

  /**
   * Handles full page replacement with new filter results
   * Handles mobile filter tree state preservation directly
   *
   * @param domId - Target DOM element ID
   * @param html - HTML content to render
   */
  protected async handleFullPageReplace(domId: string, html: string): Promise<void> {
    const oldDOM = window.document.getElementById(domId);
    if (!oldDOM) return;

    const nextDOM = oldDOM?.cloneNode(false) as HTMLElement;

    this.filterStore.setState({ isRendering: true });

    // Check mobile wrapper state before replacement
    let mobileWrapper = oldDOM.querySelector(FILTER_MOBILE_WRAPPER_SELECTOR) as HTMLElement | null;
    if (mobileWrapper && mobileWrapper.style && mobileWrapper.style.display !== 'none') {
      let fullHeight = mobileWrapper?.querySelector(FILTER_MOBILE_FULL_HEIGHT_SELECTOR);
      if (!fullHeight) {
        if (nextDOM) {
          nextDOM.innerHTML = html;
          this.attachEventListeners(nextDOM);
          this.filterStore.setState({ document: nextDOM, oldDOM: oldDOM });
        }
      } else {
        const fullHeightHtml = fullHeight.innerHTML;
        const fullHeightId = fullHeight.id;
        if (nextDOM) {
          nextDOM.innerHTML = html;
          this.attachEventListeners(nextDOM);
          this.filterStore.setState({ document: nextDOM, oldDOM: oldDOM });
        }
        mobileWrapper = nextDOM.querySelector(FILTER_MOBILE_WRAPPER_SELECTOR) as HTMLElement | null;
        if (mobileWrapper) {
          await this.collapseMobileBtn({ elementId: fullHeightId });
          fullHeight = mobileWrapper.querySelector(`#${fullHeightId}`);
          if (fullHeight) {
            fullHeight.innerHTML = fullHeightHtml;
          }
        }
      }
      mobileWrapper = nextDOM.querySelector(FILTER_MOBILE_WRAPPER_SELECTOR) as HTMLElement | null;
      if (mobileWrapper) {
        mobileWrapper.style.display = 'unset';
      }
      await this.filterRender.lazyLoadFilterTree();
    } else {
      if (nextDOM) {
        nextDOM.innerHTML = html;
        this.attachEventListeners(nextDOM);
        this.filterStore.setState({ document: nextDOM, oldDOM: oldDOM });
      }
    }
    this.filterStore.setState({ isRendering: false });

    // Update currency symbols AFTER DOM is set in filterStore
    this.bindUiFormatCurrencyForRangeSlider();

    this.filterHelper.emit(EVENT_NAMES.FILTER_CHANGED);
  }
  /**
   * Attaches event listeners to the new DOM
   *
   * @param dom - DOM element to attach listeners to
   */
  protected attachEventListeners(dom: HTMLElement): void {
    dom.addEventListener('click', (event) => {
      this.filterHandler.handleCoreClick(event);
    });

    this.handleInCollectionSearch(dom);
  }
  /**
   * Updates infinite scroll loading state
   */
  protected updateInfiniteScrollState(): void {
    const paginationType = this.getPaginationType();
    if (paginationType === 'infinite_scroll') {
      // Reset loading state to allow next infinite scroll trigger
      this.setInfiniteScrollLoading(false);
    }
  }

  /**
   * Manages body scroll class based on filter tree visibility
   */
  protected manageBodyScrollClass(): void {
    const filterSettings = this.appService.filterSettings;
    const { filterTreeMobileStyle, filterTreeVerticalStyle, filterLayout } = filterSettings;

    const document = this.document;
    const filterTreeMobileWrapper = document.querySelector(
      '.boost-sd__filter-tree-mobile-wrapper'
    ) as HTMLElement;

    const isMobileVisible =
      filterTreeMobileWrapper?.style?.display !== 'none' &&
      filterTreeMobileStyle !== 'style1' &&
      this.isTabletPortraitMaxWidth();

    const isDesktopOffCanvas =
      !this.isTabletPortraitMaxWidth() &&
      filterTreeVerticalStyle === 'style-off-canvas' &&
      filterLayout === 'vertical';

    if (!(isMobileVisible || isDesktopOffCanvas)) {
      window.document.body.classList.remove('boost-sd__g-no-scroll');
    }
  }

  protected getPaginationType(): string {
    // TODO: Get from theme settings
    const { paginationType = 'default' } =
      this.appService.themeSettings?.additionalElements?.pagination || {};
    return paginationType;
  }

  protected setInfiniteScrollLoading(_isLoading: boolean): void {
    // TODO: Set state to context
    this.filterStore.setState({ infiniteScrollLoading: _isLoading });
  }

  protected isTabletPortraitMaxWidth(): boolean {
    // TODO: Implement viewport check
    return window.innerWidth <= 768;
  }

  /**
   * Transforms product prices in the DOM using IntersectionObserver for performance
   * Handles multi-variant pricing, sale prices, and currency formatting
   */
  protected transformProductPrice(): void {
    const blockType = this.filterStore.state.value['blockType'];
    if (blockType === 'filter') {
      const productList = this.filterStore
        .getState('document')
        .querySelector('.boost-sd__product-list') as HTMLElement | null;
      if (productList?.dataset?.productPrice === 'true') {
        return;
      }
    }

    const productItems = document?.querySelectorAll(
      '.boost-sd__product-item, .boost-sd__product-item-list-view-layout'
    );

    const templateSettings = this.appService.TAEAppConfig.templateSettings;

    const {
      showCentAsSuperscript = false,
      showCurrencyCodes = false,
      compareAtPricePosition = 'right',
      showSavingDisplay = false,
    } = templateSettings?.themeSettings?.productItems?.productInfo?.elements?.price || {};
    const { textAlign = 'left' } = templateSettings?.themeSettings?.productItems?.productInfo || {};

    // In SSR, field showMultiVariantPrice is removed from themeSettings so we need to check multi variant based on translation
    const productItem = this.appService.TAEAppConfig.translation?.productItem;
    const productItemAmountTranslation =
      typeof productItem === 'object' && productItem !== null ? productItem.amount : undefined;
    const productItemSavingAmountTranslation =
      typeof productItem === 'object' && productItem !== null
        ? productItem.savingAmount
        : undefined;

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(async (entry) => {
        if (entry.isIntersecting) {
          const { target: productItem } = entry;
          const productPrice = productItem.querySelector('.boost-sd__product-price');

          if (!productPrice) return;

          const dataProduct = productItem.getAttribute('data-product');
          if (!dataProduct) return;

          const dataProductObj: any = this.filterHelper.safeParseJSON(dataProduct);
          if (!dataProductObj || !Object.keys(dataProductObj)?.length) return;

          const { priceMin, priceMax, compareAtPriceMin, compareAtPriceMax } = dataProductObj;
          /** Check if min-max is same value, show one price */
          const isSamePrice =
            `${priceMin}-${compareAtPriceMin}` === `${priceMax}-${compareAtPriceMax}`;

          if (!productItemAmountTranslation || !productItemSavingAmountTranslation) {
            observer.unobserve(productItem);
            return;
          }

          const regex = /\{\{(?:\s*minPrice\s*|\bminPrice\b)\}}|{{\s*maxPrice\s*|\bmaxPrice\b}}/i;
          const hasMultiVariantPrice = regex?.test(productItemAmountTranslation);

          const noPriceMax = isSamePrice || !hasMultiVariantPrice;

          const priceHTML = await this.buildPriceHTML(
            priceMin,
            compareAtPriceMin,
            noPriceMax ? null : priceMax,
            {
              showCentAsSuperscript,
              showCurrencyCodes,
              compareAtPricePosition,
              showSavingDisplay,
              textAlign,
              productItemSavingAmountTranslation,
              productItemAmountTranslation,
            }
          );

          // Has price after transformation
          if (priceHTML && priceHTML.match(/\d/)) {
            productPrice.innerHTML = priceHTML;
          }

          observer.unobserve(productItem);
        }
      });
    });

    productItems?.forEach((productItem) => {
      observer.observe(productItem);
    });
  }

  /**
   * Builds formatted price HTML for product items
   *
   * @param priceMin - Minimum price
   * @param compareAtPriceMin - Compare at price for min
   * @param priceMax - Maximum price (for multi-variant)
   * @param options - Formatting options
   * @returns HTML string with formatted prices
   */
  protected async buildPriceHTML(
    priceMin: string,
    compareAtPriceMin: string,
    priceMax: string,
    options: {
      showCentAsSuperscript: boolean;
      showCurrencyCodes: boolean;
      compareAtPricePosition: string;
      showSavingDisplay: boolean;
      textAlign: string;
      productItemSavingAmountTranslation: string;
      productItemAmountTranslation: string;
    }
  ): Promise<string> {
    const {
      showCentAsSuperscript,
      showCurrencyCodes,
      compareAtPricePosition,
      showSavingDisplay,
      textAlign,
      productItemSavingAmountTranslation,
      productItemAmountTranslation,
    } = options;

    let html = '';

    const { isSale, salePercent, saleAmount } = this.calculateSaleInfo({
      priceMin,
      compareAtPriceMin,
    });

    const minPriceWithFormat = this.filterHelper.formatCurrency({
      value: Number(priceMin),
      showCurrencyCodes,
      showCentAsSuperscript,
      removeDecimalPoint: showCentAsSuperscript,
    });
    const maxPriceWithFormat = this.filterHelper.formatCurrency({
      value: Number(priceMax),
      showCurrencyCodes,
      showCentAsSuperscript,
      removeDecimalPoint: showCentAsSuperscript,
    });

    const saleAmountWithFormat = this.filterHelper.formatCurrency({
      value: saleAmount,
      showCurrencyCodes,
    });

    const compareAtPriceWithFormat =
      !compareAtPriceMin || compareAtPriceMin === '0' || !isSale
        ? null
        : this.filterHelper.formatCurrency({
            value: Number(compareAtPriceMin),
            showCurrencyCodes,
            showCentAsSuperscript,
            removeDecimalPoint: showCentAsSuperscript,
          });

    const savingPriceWithFormat =
      !showSavingDisplay || !saleAmount
        ? null
        : this.filterTranslationService.translateWithComponent(productItemSavingAmountTranslation, {
            salePercent,
            saleAmount: saleAmountWithFormat,
          });

    // priceMax used for multi variant price display where it can have min-max price with format and translation
    const priceValueWithFormat = priceMax
      ? this.filterTranslationService.translateWithComponent(productItemAmountTranslation, {
          minPrice: minPriceWithFormat,
          maxPrice: maxPriceWithFormat,
        })
      : minPriceWithFormat;

    const template = this.filterHelper.getTemplate('productPriceTemplate');
    html += await this.appService.templateRender(template, {
      compareAtPricePosition,
      isSale,
      textAlign,
      priceValueWithFormat,
      compareAtPriceWithFormat,
      savingPriceWithFormat,
    });

    return html;
  }

  /**
   * Calculates sale information (is on sale, sale percentage, sale amount)
   *
   * @param prices - Price information
   * @returns Sale calculation results
   */
  protected calculateSaleInfo(prices: { priceMin: string; compareAtPriceMin: string }): {
    isSale: boolean;
    salePercent: string | undefined;
    saleAmount: number | undefined;
  } {
    const priceMin = Number(prices.priceMin);
    const compareAtPriceMin = Number(prices.compareAtPriceMin);

    const isSale = (compareAtPriceMin || 0) > priceMin;

    const salePercent =
      isSale && compareAtPriceMin
        ? Math.round((((compareAtPriceMin || 0) - priceMin) * 100) / compareAtPriceMin) + '%'
        : undefined;

    const saleAmount = isSale ? (compareAtPriceMin || 0) - priceMin : undefined;

    return {
      isSale,
      salePercent,
      saleAmount,
    };
  }

  /**
   * Handles mobile filter button collapse/expand behavior
   * Manages UI state transitions for mobile filter tree styles
   *
   * @param action - Action object containing actionId and optional elementId
   */
  async collapseMobileBtn(action: { actionId?: string; elementId?: string }): Promise<void> {
    const document = this.document;
    const filterTreeParent = this.filterService.getFilterTreeParent();
    const targetElement = filterTreeParent?.querySelector(
      `#${action.elementId || `${action.actionId}-target`}`
    ) as HTMLElement | null;

    const { filterTreeMobileStyle } = this.appService.filterSettings;
    const isMobileStyle2 =
      filterTreeMobileStyle === 'style2' || filterTreeMobileStyle === 'style2-fullwidth';
    const isMobileStyle3 =
      filterTreeMobileStyle === 'style3' || filterTreeMobileStyle === 'style3-fullwidth';

    // Update refine-by title for non-style1 mobile layouts
    if (filterTreeMobileStyle !== 'style1') {
      const refineByTitle = filterTreeParent?.querySelector(
        '.boost-sd__filter-tree-vertical-toolbar-middle'
      ) as HTMLElement | null;
      const currentTitle = document.querySelector(`#${action.actionId}`) as HTMLElement | null;

      if (refineByTitle && currentTitle) {
        // Get h3 text from the button (more reliable than firstChild)
        const heading = currentTitle.querySelector('.boost-sd__filter-option-heading');
        const textContent = heading?.textContent?.trim();

        if (textContent) {
          refineByTitle.textContent = textContent;
        }
      }
    }

    // Handle clear button visibility and state for style2 and style3
    if (isMobileStyle2) {
      const domClearAllButton = document.querySelector(
        FILTER_TREE_MOBILE_BUTTON_CLEAR
      ) as HTMLElement | null;

      const selectedFilters = this.filterStore.getState('selectedFilter') || [];
      // Get original filterOptionId (with ::) from DOM data attribute, not from encoded actionId
      const domFilterOption = targetElement?.closest(
        '.boost-sd__filter-option'
      ) as HTMLElement | null;
      const filterOptionId = domFilterOption?.dataset?.filteroptionid;
      const existKey = Array.isArray(selectedFilters)
        ? selectedFilters.find(
            (item: { metaData?: { key: string } }) => item.metaData?.key === filterOptionId
          )
        : null;

      if (filterOptionId) {
        this.filterStore.setState({
          filterOptionIdMobileSelected: { level: 2, key: filterOptionId },
        });
      }

      if (existKey && domClearAllButton) {
        const clearButton = this.filterRender.createButtonClearOrClearAll('clear', filterOptionId);
        domClearAllButton.replaceWith(clearButton);
      } else if (domClearAllButton) {
        domClearAllButton.classList.add('boost-sd__g-hide');
      }
    }

    if (isMobileStyle3) {
      const selectedFilters = this.filterStore.getState('selectedFilter') || [];

      if (selectedFilters.length > 0) {
        const clearAllButton = this.filterRender.createButtonClearOrClearAll('clearAll');
        const clearButton = document.querySelector(
          FILTER_TREE_MOBILE_BUTTON_CLEAR
        ) as HTMLElement | null;
        if (!clearButton) {
          const toolBarMobile = document.querySelector(FILTER_TREE_MOBILE_TOOL_BAR);
          toolBarMobile?.append(clearAllButton);
        } else {
          clearButton.replaceWith(clearAllButton);
        }
      }
    }

    // Toggle filter option labels visibility for style2
    const mobileWrapper = document.querySelector(FILTER_MOBILE_WRAPPER_SELECTOR);
    const filterLabels = mobileWrapper?.querySelectorAll('.boost-sd__filter-option-label');
    filterLabels?.forEach((element) => {
      if (isMobileStyle2) {
        element.classList.toggle('boost-sd__filter-option-label--hidden');
      }
    });

    if (targetElement) {
      // Toggle aria-expanded attribute
      const buttonFilterOptionToggle = targetElement
        .closest('.boost-sd__filter-option')
        ?.querySelector('.boost-sd__filter-option-title') as HTMLElement | null;

      this.filterRender.toggleAriaExpanded(buttonFilterOptionToggle);

      // Handle close button visibility for style2
      if (filterTreeMobileStyle === 'style2') {
        const closeButton = document.querySelector(FILTER_MOBILE_BUTTON_CLOSE_CLASS);
        closeButton?.classList.remove(FILTER_MOBILE_BUTTON_HIDDEN);
      }

      // Apply style2-specific classes
      if (isMobileStyle2) {
        targetElement.classList.toggle(FILTER_MOBILE_FULL_HEIGHT_CLASS);
        targetElement.classList.toggle(FILTER_OPTION_ITEM_WRAPPER_HIDDEN_CLASS);
        targetElement.classList.remove(FILTER_OPTION_ITEM_WRAPPER_COLLAPSED_CLASS);

        const backButton = document.querySelector(FILTER_MOBILE_BUTTON_CLOSE_CLASS);
        backButton?.classList.add(FILTER_MOBILE_BUTTON_BACK);
        backButton?.classList.remove(FILTER_MOBILE_BUTTON_CLOSE);
      }

      // Render full filter option if needed
      const domList = targetElement.querySelector('.boost-sd__filter-option-listitem');
      if (domList) {
        const domFilterOption = domList.closest('.boost-sd__filter-option') as HTMLElement | null;
        const filterOptionId = domFilterOption?.dataset?.filteroptionid;

        if (filterOptionId) {
          const option = this.getFilterOptionById(filterOptionId);
          if (option) {
            await this.filterRender.renderFullFilterOption(domList as HTMLElement, option);
          }
        }
      }
    }
  }

  /**
   * Gets a filter option by its ID from the filter tree state
   *
   * @param id - Filter option ID
   * @returns Filter option object or undefined
   */
  protected getFilterOptionById(id: string): any {
    const filterTree = this.filterStore.getState('filter');
    const options = filterTree?.options;
    if (!Array.isArray(options)) return undefined;
    return options.find((option: any) => option.filterOptionId === id);
  }

  /**
   * Toggles the collapse state of a filter option
   * Manages collapse/expand behavior for both mobile and desktop layouts
   *
   * @param action - Action object containing actionId and targets configuration
   */
  filterOptionCollapse(action: {
    actionId: string;
    elementId?: string;
    targets?: Array<{
      targetId: string;
      classToggle: string;
      shouldAddClass?: boolean;
    }>;
  }): void {
    // Handle mobile collapse behavior
    const tabletBreakpoint = parseInt(
      (this.appService.TAEAppConfig.generalSettings as { breakpointtabletportraitmax?: string })
        ?.breakpointtabletportraitmax || '768',
      10
    );
    const isMobileSize = this.filterHelper.isMobile(tabletBreakpoint);
    if (isMobileSize) {
      this.collapseMobileBtn({ actionId: action.actionId, elementId: action.elementId });
    }
    const { filterLayout } = this.appService.filterSettings;
    const collapseState =
      (this.filterStore.state.value[COLLAPSE_STATE] as Record<string, any>) || {};

    if (Object.keys(collapseState).length === 0) return;

    const updatedCollapseState = { ...collapseState };

    // horizontal close filterOption opening
    if (filterLayout === 'horizontal') {
      Object.keys(updatedCollapseState).forEach((key) => {
        const targets = updatedCollapseState[key]?.targets || [];
        if (
          targets &&
          targets.length > 0 &&
          key !== action.actionId &&
          !targets[0].shouldAddClass &&
          targets[1]?.shouldAddClass
        ) {
          updatedCollapseState[key] = this.getFilterOptionToggleState(updatedCollapseState[key]);
        }
      });
    }

    // set toggle current filterOption
    // Skip aria-expanded toggle when collapseMobileBtn already handled it (mobile/tablet)
    updatedCollapseState[action.actionId] = this.getFilterOptionToggleState(
      updatedCollapseState[action.actionId],
      { skipAriaToggle: isMobileSize }
    );

    if (updatedCollapseState[action.actionId]) {
      this.filterStore.setState({ [COLLAPSE_STATE]: updatedCollapseState });
    }
    this.filterHelper.emit(EVENT_NAMES.COLLAPSE_CHANGED);
  }

  /**
   * Gets the toggled state for a filter option
   * Updates target elements and toggles aria-expanded attributes
   *
   * @param currentAction - Current action configuration with targets
   * @returns Updated action with toggled states
   */
  protected getFilterOptionToggleState(
    currentAction: {
      actionId?: string;
      filterLayout?: string;
      targets?: Array<{
        targetId: string;
        classToggle: string;
        shouldAddClass: boolean;
      }>;
    },
    options?: { skipAriaToggle?: boolean }
  ): typeof currentAction {
    const filterTreeParent = this.filterService.getFilterTreeParent();
    let targets = currentAction?.targets || [];

    if (targets.length > 0) {
      targets = targets.map((target) => {
        const targetElement = filterTreeParent?.querySelector(
          `#${target.targetId}`
        ) as HTMLElement | null;
        if (targetElement) {
          if (!options?.skipAriaToggle) {
            const buttonFilterOptionToggle = targetElement.querySelector(
              '.boost-sd__filter-option-title'
            ) as HTMLElement | null;
            this.filterRender.toggleAriaExpanded(buttonFilterOptionToggle);
          }

          return {
            ...target,
            shouldAddClass: !target.shouldAddClass,
          };
        }

        return target;
      });

      currentAction.targets = targets;
    }

    return currentAction;
  }

  /**
   * Fetches and updates products based on in-collection search input.
   * Triggers search when user types in the collection search box.
   *
   * This method:
   * - Resets pagination to page 1
   * - Updates URL parameters with search query
   * - Disables input during fetch to prevent concurrent requests
   * - Fetches filtered results from the API
   * - Dispatches analytics event for search
   * - Re-enables input after completion
   *
   * @param urlParams - URL search parameters to update with search query
   * @param inputElement - The search input element to disable/enable
   *
   * @remarks
   * This method is called by handleInCollectionSearch after debouncing user input.
   * The input is temporarily disabled to provide visual feedback and prevent
   * race conditions from rapid typing.
   *
   * @example
   * Override to add custom search behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     async fetchInCollectionSearch(urlParams, inputElement) {
   *       // Add loading indicator
   *       inputElement.classList.add('searching');
   *
   *       // Track search in analytics
   *       this.trackSearchQuery(urlParams.get('q'));
   *
   *       await super.fetchInCollectionSearch(urlParams, inputElement);
   *
   *       inputElement.classList.remove('searching');
   *     }
   *   };
   * });
   * ```
   */
  protected async fetchInCollectionSearch(
    urlParams: URLSearchParams,
    inputElement: HTMLInputElement
  ) {
    const paginationValue = this.filterStore.state.value['pagination'];
    this.filterStore.setState({ pagination: { ...paginationValue, page: 1 } });

    urlParams.delete('page');

    window.history.replaceState({}, '', `${window.location.pathname}?${urlParams}`);
    // Disable input to prevent further typing
    inputElement.disabled = true;
    await this.handleInCollectionSearchFilter();

    const q = urlParams.get('q') || '';
    this.filterHelper.emit(EVENT_NAMES.DEBOUNCE_INCOLLECTION_SEARCH, { searchTerm: q });

    // Enable input back after search completes
    inputElement.disabled = false;
  }

  /**
   * Sets up in-collection search functionality.
   * Initializes search input behavior including debounced search and clear button.
   *
   * This method:
   * - Sets up debounced keyup listener on search input (default 500ms delay)
   * - Handles search query URL parameter updates
   * - Manages input focus and cursor position on re-render
   * - Sets up clear button click handler
   * - Ensures input remains in viewport when focusing
   *
   * @param _dom - The DOM context containing the search input element
   *
   * @remarks
   * The search delay can be customized via `generalSettings.inCollectionSearchDelay`.
   * The method preserves input focus and cursor position using the `data-input-focusing`
   * attribute to improve UX during filter updates.
   *
   * @example
   * Override to customize search behavior:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     handleInCollectionSearch(_dom) {
   *       super.handleInCollectionSearch(_dom);
   *
   *       // Add search suggestions dropdown
   *       const input = _dom.querySelector('.boost-sd__in-collection-search-input');
   *       input?.addEventListener('input', () => {
   *         this.showSearchSuggestions(input.value);
   *       });
   *     }
   *   };
   * });
   * ```
   */
  protected async handleInCollectionSearch(_dom?: HTMLElement): Promise<void> {
    // TODO: Import from in-collection-search module
    _dom = _dom || this.document;
    const inCollectionSearch = _dom.querySelector(
      '.boost-sd__in-collection-search-input'
    ) as HTMLInputElement | null;
    if (inCollectionSearch) {
      const urlParams = new URLSearchParams(window.location.search);
      const dataInputFocusing = inCollectionSearch.hasAttribute('data-input-focusing');
      const clearBtn = _dom.querySelector('.boost-sd__in-collection-search-clear-btn');

      if (dataInputFocusing) {
        const end = inCollectionSearch.value.length;
        inCollectionSearch.setSelectionRange(end, end);

        // Only re-focus when the element is in the viewport to avoid scrolling to top
        if (this.domHelper.isElementInViewport(inCollectionSearch)) {
          inCollectionSearch.focus();
        }
      }

      const delay =
        (this.appService.TAEAppConfig.generalSettings as any)?.inCollectionSearchDelay || 500;

      inCollectionSearch?.addEventListener(
        'keyup',
        this.filterHelper.debounce(async (event) => {
          // set 'q' param in the URL
          if (event.target.value === '') {
            urlParams.delete('q');
          } else {
            urlParams.set('q', event.target.value);
          }
          await this.fetchInCollectionSearch(urlParams, inCollectionSearch);
        }, delay)
      );
      clearBtn?.addEventListener('click', async () => {
        urlParams.delete('q');
        await this.fetchInCollectionSearch(urlParams, inCollectionSearch);
      });
    }
  }

  /**
   * Renders product recommendations on search result pages.
   * Triggers recommendation widget rendering for search pages with results.
   *
   * This method is an integration hook that delegates to the dynamicBundle module
   * to render product recommendations based on search context.
   *
   * @remarks
   * This method is called automatically during search page initialization and
   * after filter updates. The actual recommendation logic is handled by the
   * dynamicBundle module to keep concerns separated.
   *
   * @example
   * Override to customize search page recommendations:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     renderRecommendationForSearchPage() {
   *       // Only show recommendations for specific search queries
   *       const searchQuery = this.filterStore.getState('searchQuery');
   *       if (this.shouldShowRecommendations(searchQuery)) {
   *         super.renderRecommendationForSearchPage();
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected renderRecommendationForSearchPage(): void {
    // TODO: Import from dynamicBundle module
    const block = this.appService.blockByType('filter');
    renderRecommendationForSearchPage(block as any);
  }

  /**
   * Renders volume bundle widgets on search result pages.
   * Dynamically imports and initializes volume bundle rendering for search context.
   *
   * This method uses dynamic import (code splitting) to load the volume-bundle
   * module only when needed, reducing initial bundle size.
   *
   * @remarks
   * The volume-bundle module is loaded asynchronously via webpack's dynamic import
   * with chunk name 'volume-bundle'. This keeps the main filter bundle lightweight
   * while providing bundle functionality when required.
   *
   * @example
   * Override to customize volume bundle rendering:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     renderVolumeBundleForSearchPage() {
   *       // Only render for specific product types
   *       const hasEligibleProducts = this.checkForBundleEligibleProducts();
   *       if (hasEligibleProducts) {
   *         super.renderVolumeBundleForSearchPage();
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected renderVolumeBundleForSearchPage(): void {
    // TODO: Import dynamically from volume-bundle module
    const block = this.appService.blockByType('filter');

    import(
      /* webpackChunkName: 'volume-bundle' */
      '../../volume-bundle'
    ).then((module) => {
      const { renderVolumeBundleForSearchPage } = module;
      renderVolumeBundleForSearchPage(block as any);
    });
  }

  /**
   * Executes third-party app integrations for the filter.
   * Triggers integration callbacks for external apps and custom scripts.
   *
   * This method is an extension hook that allows third-party apps to integrate
   * with the filter system by executing registered integration callbacks.
   *
   * @remarks
   * This method is called after DOM updates to ensure third-party scripts can
   * access the latest filter state and product elements. Common integrations
   * include review apps, wishlist features, and custom tracking scripts.
   *
   * @example
   * Override to add custom integration logic:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     appIntegration() {
   *       super.appIntegration();
   *
   *       // Initialize custom review app integration
   *       if (window.CustomReviewApp) {
   *         window.CustomReviewApp.init({
   *           container: this.document,
   *           productSelector: '.boost-sd__product-item'
   *         });
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected appIntegration(): void {
    // TODO: Import from integration module
    appIntegration();
  }

  /**
   * Adjusts tooltip position to ensure it's visible within the viewport.
   * Calculates and applies proper positioning for tooltip elements.
   *
   * This method:
   * - Positions tooltip relative to its trigger element
   * - Handles edge cases when tooltip would overflow viewport
   * - Supports tooltips within slick carousel slides
   * - Applies responsive positioning based on available space
   *
   * @param tooltipContent - The tooltip element to position
   * @param rootElement - The element that triggered the tooltip
   *
   * @remarks
   * Uses fixed positioning for consistent placement and handles special cases
   * like carousels where the parent slide may have transforms applied. Adjusts
   * alignment (left/right/center) based on viewport boundaries.
   *
   * @example
   * Override to customize tooltip positioning:
   * ```javascript
   * window.boostWidgetIntegration.extend('FilterController', (FilterController) => {
   *   return class CustomFilterController extends FilterController {
   *     adjustTooltipPosition(tooltipContent, rootElement) {
   *       super.adjustTooltipPosition(tooltipContent, rootElement);
   *
   *       // Add arrow pointer to tooltip
   *       const arrow = tooltipContent.querySelector('.tooltip-arrow');
   *       if (arrow) {
   *         const rootRect = rootElement.getBoundingClientRect();
   *         arrow.style.left = `${rootRect.width / 2}px`;
   *       }
   *     }
   *   };
   * });
   * ```
   */
  protected adjustTooltipPosition = (tooltipContent, rootElement) => {
    if (!tooltipContent || !rootElement) return;

    const position = rootElement.getBoundingClientRect();

    const slideAsParent = tooltipContent.closest('.slick-slide');

    if (slideAsParent) {
      const { height: tooltipHeight, y: tooltipY } = rootElement.getBoundingClientRect();
      const { y: slideY } = slideAsParent.getBoundingClientRect();
      const transformY = tooltipY - slideY < tooltipHeight ? '35px' : '-35px';

      let transform = `translate(calc(-50% + ${position.width / 2}px), ${transformY})`;

      Object.assign(tooltipContent.style, {
        position: 'fixed',
        display: 'block',
        transform,
      });

      const { width: tooltipWidth, x: tooltipX } = tooltipContent.getBoundingClientRect();

      if (slideAsParent.classList.contains('slick-current')) {
        const { x: slideX } = slideAsParent.getBoundingClientRect();
        if (tooltipX - slideX < tooltipWidth) {
          transform = `translate(0px, ${transformY})`;
        }
      }

      if (
        slideAsParent.classList.contains('slick-active') &&
        !slideAsParent.nextElementSibling?.classList.contains('slick-active')
      ) {
        const { x: slideX, width: slideWidth } = slideAsParent.getBoundingClientRect();
        if (slideX + slideWidth - tooltipX < tooltipWidth) {
          transform = `translate(-50%, ${transformY})`;
        }
      }

      Object.assign(tooltipContent.style, {
        position: 'fixed',
        display: 'block',
        transform,
      });

      return;
    }

    let left = `${position.left}px`;
    const transform = `translate(calc(-50% + ${position.width / 2}px), calc(-100% - 6px))`;

    Object.assign(tooltipContent.style, {
      position: 'fixed',
      left,
      top: `${position.top}px`,
      display: 'block',
      transform,
    });

    const positionContent = tooltipContent?.getBoundingClientRect();

    if (positionContent.width / 2 > position.left) {
      left = `${positionContent.width / 2}px`;
      Object.assign(tooltipContent.style, {
        left,
      });
    }

    if (
      position.right + positionContent.width / 2 > window.innerWidth &&
      position.width < positionContent.width
    ) {
      const right = `${position.width / 2 - positionContent.width / 2 + 5}px`;
      left = `auto`;
      Object.assign(tooltipContent.style, {
        left,
        right,
      });
    }
  };

  /**
   * Initializes tooltip functionality for product items.
   *
   * Sets up mouseenter/mouseleave event listeners to show/hide tooltips
   * and adjusts their positioning.
   *
   * @example
   * Override to customize tooltip behavior:
   * ```typescript
   * protected handleTooltip(): void {
   *   super.handleTooltip();
   *   // Add custom tooltip animations
   *   document.querySelectorAll('.boost-sd__tooltip').forEach(tooltip => {
   *     tooltip.classList.add('custom-animation');
   *   });
   * }
   * ```
   */
  protected handleTooltip(): void {
    // TODO: Import from tooltip module
    const document = this.document;
    const tooltips = document.querySelectorAll('.boost-sd__tooltip');

    tooltips?.forEach((tooltip) => {
      tooltip?.addEventListener('mouseenter', () => {
        const tooltipContent = tooltip?.querySelector('.boost-sd__tooltip-content');
        this.adjustTooltipPosition(tooltipContent, tooltip);
      });

      tooltip?.addEventListener('mouseleave', () => {
        const tooltipContent = tooltip?.querySelector(
          '.boost-sd__tooltip-content'
        ) as HTMLElement | null;
        if (tooltipContent) {
          Object.assign(tooltipContent.style, {
            display: 'none',
          });
        }
      });
    });
  }

  /**
   * Handles filter reset operation.
   *
   * Resets all active filters and updates the DOM with the cleared filter state.
   * Override to add custom reset behavior.
   *
   * @example
   * Override to track filter resets:
   * ```typescript
   * protected async handleResetFilter(): Promise<void> {
   *   this.trackAnalyticsEvent('filter_reset');
   *   await super.handleResetFilter();
   * }
   * ```
   */
  protected async handleResetFilter(): Promise<void> {
    const result = (await this.filterService.resetFilters()) as FilterAPIResponse | null;
    if (result) {
      const { html } = result;
      const transformedHTML = this.transformFilterTreeHTML(html);
      const domId = this.filterStore.state.value['id'];
      this.updateFilterDOM(domId, transformedHTML);
    }
  }

  protected async handleInCollectionSearchFilter(): Promise<void> {
    const result = (await this.filterService.fetchFilters({
      inCollectionSearchFocusing: true,
    })) as FilterAPIResponse | null;
    if (result) {
      const { html } = result;
      const transformedHTML = this.transformFilterTreeHTML(html);
      const domId = this.filterStore.state.value['id'];
      this.updateFilterDOM(domId, transformedHTML);
    }
  }

  /**
   * Fetches filter results from the API and updates the DOM.
   *
   * @param addition - Additional parameters for the filter API request
   *
   * @example
   * Override to add custom API behavior:
   * ```typescript
   * protected async handleFetchFilter(addition = {}) {
   *   // Add custom parameters for specific shop
   *   addition.customParam = this.getShopSpecificParam();
   *   await super.handleFetchFilter(addition);
   * }
   * ```
   */
  protected async handleFetchFilter(addition: FilterAPIAdditionParams = {}): Promise<void> {
    const result = (await this.filterService.fetchFilters(addition)) as FilterAPIResponse | null;
    if (result) {
      const { html } = result;
      const transformedHTML = this.transformFilterTreeHTML(html);
      const domId = this.filterStore.state.value['id'];
      this.updateFilterDOM(domId, transformedHTML, addition?.behavior);
    }
  }

  async handleExceededPage() {
    // reset filter request if page exceeded total page - delegate to service
    await this.filterService.handleExceededPage();
  }

  async renderFilterTreeOnStateChanged() {
    const filterState = this.filterStore.getState('filter');
    if (!filterState) return;

    // Add collection filter tree change detection
    const { filterTreeMobileStyle } = this.appService.filterSettings;
    const isMobileStyle2 =
      filterTreeMobileStyle === 'style2' || filterTreeMobileStyle === 'style2-fullwidth';

    /**
     * Checks if mobile filter tree needs to be reset when filter options change
     * Resets mobile filter visibility when a selected option no longer exists or has no values
     */
    const resetMobileFilter = (filterResetTimeoutId, previousFilterOptions, isMobileStyle2) => {
      // Early return if we're not on mobile or not using Style2
      if (!this.filterHelper.isMobile() || !isMobileStyle2) {
        return filterResetTimeoutId;
      }

      // Get the currently selected filter option ID
      const selectedData = this.filterStore.getState('filterOptionIdMobileSelected') || {};
      const selectedFilterOptionId = selectedData.key;

      // If we had a selected filter option and filter tree options exist
      if (
        selectedFilterOptionId &&
        previousFilterOptions &&
        previousFilterOptions.length > 0 &&
        filterState.options.length > 0
      ) {
        // Check if the selected filter option still exists AND has values in the new filter tree
        const selectedOptionExists = filterState.options.some(
          (option) =>
            option.filterOptionId === selectedFilterOptionId &&
            option.values &&
            option.values.length > 0
        );

        // If the selected filter option no longer exists, reset mobile filter visibility
        if (!selectedOptionExists) {
          // Clear any pending reset to prevent redundant calls
          if (filterResetTimeoutId) {
            clearTimeout(filterResetTimeoutId);
          }
          // Schedule a new reset
          filterResetTimeoutId = setTimeout(() => {
            this.filterRender.backFilterTreeMobile();
            filterResetTimeoutId = null; // Clear the ID after execution
          }, FILTER_TREE_RESET_DELAY);
        }
      }

      return filterResetTimeoutId;
    };
    let filterResetTimeoutId = this.filterStore.getState('filterResetTimeoutId') || null;
    let previousFilterOptions = this.filterStore.getState('previousFilterOptions') || null;
    filterResetTimeoutId = resetMobileFilter(
      filterResetTimeoutId,
      previousFilterOptions,
      isMobileStyle2
    );

    // Store current filter options for next comparison and persist both values in the store
    previousFilterOptions = [...(filterState?.options || [])];
    this.filterStore.setState({ filterResetTimeoutId, previousFilterOptions });

    await this.filterRender.lazyLoadFilterTree();

    // register when search/filter in search page
    const block = this.appService.blockByType('filter');
    if (this.filterHelper.isSearchPage()) {
      renderRecommendationForSearchPage(block as any);
      import(
        /* webpackChunkName: 'volume-bundle' */
        '../../volume-bundle'
      ).then((module) => {
        const { renderVolumeBundleForSearchPage } = module;
        renderVolumeBundleForSearchPage(block as any);
      });
    }

    const { collection_handle, page } = this.appService.TAEAppConfig.generalSettings || {};

    if (page === 'collection' && collection_handle) {
      // Delegate to service
      this.filterService.saveCollectionInfo();
    }

    this.filterRender.renderCollectionHeader();
  }

  async handleFilterTree() {
    window.addEventListener(
      'resize',
      this.filterHelper.debounce(() => {
        this.filterRender.handleFilterTreeResponsive();
      }, 100)
    );

    await this.renderFilterTreeOnStateChanged();

    window.addEventListener('afterRenderHtmlForFilter', () => {
      const currentFocus = this.filterStore.getState(FILTER_TREE_STATE_KEY.CURRENT_FOCUS);
      const currentFocusElement = document.getElementById(currentFocus);

      const { filterLayout, filterTreeHorizontalStyle } = this.appService.filterSettings;

      const { paginationType = 'default' } =
        this.appService.themeSettings?.additionalElements?.pagination || {};

      // Add check for search input
      const isSearching = document.activeElement?.matches(FILTER_OPTION_SEARCH_OPTION_SELECTOR);

      // Don't change focus if user is searching
      if (isSearching) {
        return;
      }

      if (paginationType === 'default') {
        if (filterLayout === 'horizontal' && filterTreeHorizontalStyle !== 'style-expand') {
          (
            currentFocusElement
              ?.closest('.boost-sd__filter-option')
              ?.querySelector('.boost-sd__filter-option-title') as HTMLElement | null
          )?.focus();
        } else if (this.filterHelper.isEnterOrSpaceKeyPressed()) {
          currentFocusElement?.setAttribute('tabindex', '0');
          currentFocusElement?.focus();
        } else {
          (
            currentFocusElement?.closest('.boost-sd__filter-tree-wrapper') as HTMLElement | null
          )?.focus();
        }
      }
    });
  }

  //#region Bulk Quantity
  async handleQuantityChange(variantId, quantity) {
    const data = await CartAPI.getAll();
    const item = data.items.find((item) => item.variant_id === parseInt(variantId, 10));

    if (quantity === 0 || !item) {
      await CartAPI.add(variantId, quantity);
    } else {
      await CartAPI.change(variantId, quantity);
    }

    this.filterStore.setState({ forceRender: !this.filterStore.state.value.forceRender });
  }

  handleQuantityInput(quantityInput, progressBarContainer, minQuantity, variantId) {
    quantityInput.addEventListener('change', async () => {
      const currentQuantity = parseInt(quantityInput.value, 10);

      if (currentQuantity < minQuantity) {
        quantityInput.setCustomValidity(`Please enter a quantity of at least ${minQuantity}`);
        quantityInput.reportValidity();
      } else {
        quantityInput.setCustomValidity('');
      }

      progressBarContainer.classList.remove('boost-sd__progress-bar-container--hidden');

      if (Number.isInteger(currentQuantity) && currentQuantity > 0) {
        await this.handleQuantityChange(variantId, currentQuantity);
      }

      progressBarContainer.classList.add('boost-sd__progress-bar-container--hidden');
    });
  }

  handleButtonClick = (
    quantityInput,
    progressBarContainer,
    decreaseButton,
    quantityStep,
    variantId,
    operation
  ) => {
    const handleButtonClickEvent = async (event) => {
      const currentQuantity = parseInt(quantityInput.value, 10);

      if (operation === 'decrease') {
        if (currentQuantity >= quantityStep) {
          quantityInput.value = currentQuantity - quantityStep;
        } else {
          quantityInput.value = 0;
        }
      } else if (operation === 'increase') {
        quantityInput.value = currentQuantity + quantityStep;
      }

      progressBarContainer.classList.remove('boost-sd__progress-bar-container--hidden');

      if (currentQuantity === 0) {
        decreaseButton.disabled = true;
        decreaseButton.setAttribute('disabled', 'disabled');
      } else {
        decreaseButton.disabled = false;
        decreaseButton.removeAttribute('disabled');
      }

      await this.handleQuantityChange(variantId, parseInt(quantityInput.value, 10));

      progressBarContainer.classList.add('boost-sd__progress-bar-container--hidden');
    };

    return handleButtonClickEvent;
  };

  async handleForceRenderChange() {
    const document = this.document;
    const bulkQuantityContainers = document.querySelectorAll('.boost-sd__bulk-quantity');

    const data = await CartAPI.getAll();
    const itemCount = data.items.reduce((acc, item) => acc + item.quantity, 0);

    const cartSelectorAndUpdateAction: any = getCartSelectorAndUpdateAction();
    cartSelectorAndUpdateAction?.action(itemCount);

    bulkQuantityContainers.forEach((container) => {
      const variantId = container.getAttribute('data-quantity-variant-id');
      const item = data.items.find(
        (item) => item.variant_id === parseInt(variantId ? variantId : '10', 10)
      );

      if (item) {
        const quantityInput = container.querySelector(
          '.boost-sd__bulk-quantity-field'
        ) as HTMLInputElement | null;
        if (quantityInput) {
          quantityInput.value = item.quantity;
        }
      }
    });
  }

  async handleBulkQuantity() {
    const document = this.document;
    const bulkQuantityContainers = document.querySelectorAll('.boost-sd__bulk-quantity');
    const data = await CartAPI.getAll();

    bulkQuantityContainers.forEach((container) => {
      const decreaseButton = container.querySelector(
        '.boost-sd__bulk-quantity-button--decrease'
      ) as HTMLButtonElement | null;
      const increaseButton = container.querySelector('.boost-sd__bulk-quantity-button--increase');
      const quantityInput = container.querySelector(
        '.boost-sd__bulk-quantity-field'
      ) as HTMLInputElement | null;
      const progressBarContainer = container.querySelector('.boost-sd__progress-bar-container');
      const quantityStepStr = quantityInput?.getAttribute('step');
      const quantityStep = quantityStepStr ? parseInt(quantityStepStr, 10) : 10;
      const quantityInputMinStr = quantityInput?.getAttribute('min');
      const minQuantity = quantityInputMinStr ? parseInt(quantityInputMinStr, 10) : 10;
      const variantId = container.getAttribute('data-quantity-variant-id') || '10';
      const item = data.items.find((item) => item.variant_id === parseInt(variantId, 10));

      if (quantityInput) {
        if (item) {
          quantityInput.value = item.quantity;
        } else {
          quantityInput.value = '0';
        }
      }

      if (decreaseButton && quantityInput && increaseButton) {
        if (quantityInput.value === '0') {
          decreaseButton.disabled = true;
          decreaseButton.setAttribute('disabled', 'disabled');
        } else {
          decreaseButton.disabled = false;
          decreaseButton.removeAttribute('disabled');
        }
        this.handleQuantityInput(quantityInput, progressBarContainer, minQuantity, variantId);

        decreaseButton.addEventListener(
          'click',
          this.handleButtonClick(
            quantityInput,
            progressBarContainer,
            decreaseButton,
            quantityStep,
            variantId,
            'decrease'
          )
        );
        increaseButton.addEventListener(
          'click',
          this.handleButtonClick(
            quantityInput,
            progressBarContainer,
            decreaseButton,
            quantityStep,
            variantId,
            'increase'
          )
        );
      }
    });

    this.filterHelper.emit(EVENT_NAMES.FORCE_RENDER_CHANGED);
  }
  //#endregion

  //#region pagination

  updateProductCount(limit, totalProduct) {
    // Get current page from pagination state instead of session storage
    const currentPage = this.filterStore.getState('pagination')?.page || 1;

    // In load more mode: from=1 (always), to=currentPage*limit (cumulative)
    // In default pagination: from=(currentPage-1)*limit+1, to=currentPage*limit
    const { paginationType = 'default' } =
      this.appService.themeSettings?.additionalElements?.pagination || {};

    let from, to;
    if (paginationType === 'load_more' || paginationType === 'infinite_scroll') {
      // Load more mode shows cumulative: "1-48 of 80"
      from = 1;
      to = Math.min(currentPage * limit, totalProduct);
    } else {
      from = (currentPage - 1) * limit + 1;
      to = Math.min(currentPage * limit, totalProduct);
    }

    const translationData = {
      from,
      to,
      total: totalProduct,
      count: totalProduct,
    };

    const paginationConfig = this.getProductCountTranslationConfig('pagination', totalProduct);
    const toolbarConfig = this.getProductCountTranslationConfig('toolbar', totalProduct);

    this.updateTextContent(paginationConfig, translationData);
    this.updateTextContent(toolbarConfig, translationData);
  }

  /**
   * @type {import('./index.d').getProductCountTranslationConfig}
   */
  getProductCountTranslationConfig = (position, totalProduct) => {
    const isToolbar = position === 'toolbar';
    const baseKey = isToolbar ? 'Toolbar' : 'Pagination';
    const isPlural = totalProduct > 1;
    let key = `productCount.textDescription${baseKey}`;
    let fallback = isToolbar
      ? '{{count}} product'
      : 'Showing {{from}} - {{to}} of {{total}} product';

    if (isPlural) {
      key += 'Plural';
      fallback += 's';
    }

    return {
      element: isToolbar
        ? this.document.querySelector('.boost-sd__toolbar-item .boost-sd__product-count')
        : this.document.querySelector('.boost-sd__product-count-pagination'),
      key,
      fallback,
    };
  };

  updateTextContent = ({ element, key, fallback }, translationData) => {
    if (!element) return;
    const content = this.filterTranslationService.translateWithComponent(
      this.filterTranslationService.translateByKey(key, fallback),
      translationData
    );
    setTimeout(() => {
      element.textContent = content;
    }, 500);
  };

  handlePaginationLoadMoreButtons = (currentPage) => {
    const document = this.document;
    const paginationLoadPreviousButton = document.querySelector(
      '.boost-sd__pagination-load-more-container-item[data-position="top"], .boost-sd__pagination-infinite-scroll-container-button'
    ) as HTMLButtonElement | null;

    const productCountPrev = document.querySelector(
      '.boost-sd__product-count[data-position="top"]'
    ) as HTMLElement | null;

    const paginationLoadMoreButton = document.querySelector(
      '.boost-sd__pagination-load-more-container-item[data-position="bottom"]'
    ) as HTMLButtonElement | null;

    const productCountNext = document.querySelector(
      '.boost-sd__product-count[data-position="bottom"]'
    ) as HTMLElement | null;

    const limit = this.filterStore.getState('defaultParams')?.limit || 24;

    let totalPage = 0;
    let totalProduct = 0;
    const productList = document.querySelector('.boost-sd__product-list');

    if (productList) {
      totalPage = Number(productList.getAttribute('data-total-page'));
      totalProduct = Number(productList.getAttribute('data-total-product'));
    }

    if (currentPage <= 1) {
      if (paginationLoadPreviousButton) paginationLoadPreviousButton.style.display = 'none';
      if (productCountPrev) productCountPrev.style.display = 'none';
    }
    if (currentPage === totalPage) {
      if (paginationLoadMoreButton) paginationLoadMoreButton.style.display = 'none';
      if (productCountNext) productCountNext.style.display = 'none';
    }

    this.updateProductCount(limit, totalProduct);
  };
  async processPaginatingAction(newPage, behavior) {
    const generalSettings = this.appService.TAEAppConfig.generalSettings || {};
    const { paginationTypeAdvanced = true } = generalSettings as any;
    const { paginationType = 'default' } =
      this.appService.themeSettings.additionalElements?.pagination || {};

    const widgetId =
      paginationType === 'default'
        ? this.filterStore.getState('widgetId')
        : this.appService.TAEAppConfig.templateMetadata.productList;

    behavior = paginationType !== 'default' ? behavior : 'refresh';

    // Session storage for NEXT_PAGE/PREV_PAGE is not needed in load more mode
    // We can rely on pagination state directly

    const { isShortenUrlParam } = this.appService.filterSettings;
    const paginationState = this.filterStore.getState('pagination') || {};

    if (newPage !== Number(paginationState.page)) {
      // Update state
      this.filterStore.setState({
        pagination: {
          ...paginationState,
          page: newPage,
        },
      });

      // Update URL
      // Always remove page parameter when newPage = 1 (clean URL for page 1)
      if (newPage === 1) {
        this.filterHelper.deleteQueryParamHistory('page', {
          preventPushHistory: false,
          isShortenUrlParam,
          urlScheme: this.filterHelper.getUrlScheme(),
        });
      } else {
        paginationTypeAdvanced &&
          this.filterHelper.setQueryParamHistory('page', newPage, false, {
            isShortenUrlParam,
            urlScheme: this.filterHelper.getUrlScheme(),
          });
      }

      this.filterHelper.setPaginationSession(newPage);

      // Fetch with widget configuration
      await this.handleFetchFilter({
        widgetId,
        behavior,
      });

      // Handle UI updates
      if (paginationType === 'default') {
        this.domHelper.scrollToTop({});
      } else {
        this.handlePaginationLoadMoreButtons(newPage);
      }
    }
  }
  handlePaginationInfiniteScroll() {
    const document = this.document;
    const target = document.querySelector(
      '.boost-sd__pagination-infinite-scroll-container-target'
    ) as HTMLElement;

    if (!target) return;

    const productList = document.querySelector('.boost-sd__product-list');
    const totalPage = productList ? Number(productList.getAttribute('data-total-page')) : 0;

    // Debounced version of the load function to handle spam
    const debouncedLoad = this.filterHelper.debounce(async (page: number) => {
      const isLoading = this.filterStore.state.value['infiniteScrollLoading'];
      if (isLoading) return; // Double-check, though unlikely needed

      this.filterStore.setState({ infiniteScrollLoading: true });

      try {
        await this.processPaginatingAction(page, 'more');
        this.filterHelper.emit(EVENT_NAMES.AFTER_DOCUMENT_CHANGED);
        this.filterHelper.emit(EVENT_NAMES.FILTER_CHANGED);
      } catch (error) {
        console.error('Pagination load failed:', error);
        // Optionally retry logic or user notification here
      } finally {
        this.filterStore.setState({ infiniteScrollLoading: false });
      }
    }, 350); // 350ms debounce delay

    this.domHelper.observeElementInViewport(
      target,
      () => {
        // Sync check first to bail early without debounce delay
        const isLoading = this.filterStore.state.value['infiniteScrollLoading'];
        if (isLoading) return;

        const currentPage = this.filterStore.getState('pagination')?.page || 1;
        if (currentPage < totalPage) {
          const nextPage = currentPage + 1;
          // Trigger debounced load
          debouncedLoad(nextPage);
        }
      },
      {
        rootMargin: '0px',
        threshold: 0.75,
      }
    );
  }
  //#endregion
}
